import React, { useState, useRef, useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Input,
  Button,
  Switch,
  Select,
  IconButton,
  Portal,
  Checkbox,
  FormControl,
  FormLabel,
  Spinner,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Textarea,
} from '@chakra-ui/react';
import { LuType, LuEraser, LuChevronDown, LuRefreshCw, LuMail, LuLinkedin, LuSettings, LuPencil, LuPlus, LuTrash2, LuChevronLeft, LuSparkles } from 'react-icons/lu';
import { FiX, FiChevronRight } from 'react-icons/fi';
import campaignService from '../../../services/campaignService';
import useSSEStream from '../../../hooks/useSSEStream';
import CampaignStatusBar from '../CampaignStatusBar';
import useCreditWarning from '../../../hooks/useCreditWarning';
import CreditWarningModal from '../../common/CreditWarningModal';

/** Message type options keyed by channel. Used for dependent dropdown. */
const MESSAGE_TYPE_OPTIONS = {
  email: [
    { value: 'new_email', label: 'New email' },
    { value: 'follow_up', label: 'Follow up' },
  ],
  linkedin: [
    { value: 'message', label: 'Message' },
    { value: 'linkedin_inmail', label: 'LinkedIn InMail' },
    { value: 'connection_message', label: 'Connection message' },
  ],
};

/** Legacy message_type → typeOfMessage mapping for backward compatibility */
const LEGACY_TYPE_MAP = {
  new_thread: 'email',
  reply: 'email',
  linkedin: 'linkedin',
};

/** Shown instead of a subject field when message type is Follow up (email). */
const EMAIL_FOLLOW_UP_THREAD_COPY =
  'Same thread, original subject, draft your follow-up below.';

/** Enable HTML row in the draft form; off until the feature is shipped. */
const SHOW_ENABLE_HTML_PANEL = false;

const AVAILABLE_VARIABLES = [
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'position', label: 'Position' },
  { key: 'company_name', label: 'Company Name' },
  { key: 'location', label: 'Location' },
  { key: 'email', label: 'Email' },
  { key: 'company_industry', label: 'Industry' },
  { key: 'company_website', label: 'Website' },
];

/**
 * Polish icon — 4-pointed sparkle (top-left) + pencil (bottom-right).
 * Mirrors Gmail's "Help me write" icon layout: AI sparkle paired with an edit tool.
 * Uses currentColor so hover/active states from the parent button work automatically.
 */
const PolishIcon = ({ size = 18, ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    {...props}
  >
    {/* 4-pointed sparkle star — filled, top-left, shifted down 1 unit closer to pencil */}
    <path
      d="M4.5 2L4.5 4.8L8.5 5.5L5.5 6.2L4.5 9L3.5 6.2L0.5 5.5L3.5 4.8Z"
      fill="currentColor"
    />
    {/* Pencil body — extended tip for slightly longer look */}
    <path
      d="M9 17L15.5 10.5L17.5 12.5L11 19H9V17Z"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {/* Pencil cap (eraser end) */}
    <path
      d="M15.5 10.5L17 8.5L19.5 11L17.5 12.5Z"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const chipStyles = `
  @keyframes polish-shimmer {
    0%   { background-position: -600px 0; }
    100% { background-position: 600px 0; }
  }
  .polish-shimmer-line {
    border-radius: 6px;
    height: 11px;
    background: linear-gradient(
      90deg,
      rgba(255,255,255,0.04) 0%,
      rgba(255,255,255,0.10) 40%,
      rgba(255,255,255,0.04) 100%
    );
    background-size: 1200px 100%;
    animation: polish-shimmer 1.6s ease-in-out infinite;
  }

  .variable-chip {
    display: inline-flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 4px;
    padding: 0 8px;
    margin: 0 2px;
    color: white;
    vertical-align: middle;
    user-select: none;
    font-weight: 400;
    height: 24px;
  }
  .chip-icon { color: #7a85ff; margin-right: 6px; display: flex; align-items: center; }
  .chip-close { 
    margin-right: 8px; 
    cursor: pointer; 
    color: #888; 
    font-size: 14px;
    display: flex;
    align-items: center;
  }
  .chip-close:hover { color: #ff5a5a; }
  .chip-text { font-size: 13px; display: flex; align-items: center; }
  .chip-nested-label {
    background: rgba(122, 133, 255, 0.2);
    padding: 0 4px;
    border-radius: 2px;
    color: #a5adff;
    margin: 0 4px;
  }

  .ai-prompt-chip {
    display: inline-flex;
    align-items: flex-start;
    cursor: text;
    background: rgba(134, 239, 172, 0.22);
    border: 1px solid rgba(74, 222, 128, 0.5);
    border-radius: 6px;
    padding: 2px 6px 2px 4px;
    margin: 0 2px;
    vertical-align: top;
    user-select: none;
    max-width: 100%;
    box-sizing: border-box;
  }
  .ai-prompt-chip-icon {
    color: #4ade80;
    margin-right: 6px;
    margin-top: 2px;
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
  }
  .ai-prompt-chip-svg { display: block; }
  .ai-prompt-chip-text {
    font-size: 13px;
    font-weight: 400;
    color: #ecfdf5;
    white-space: normal;
    word-break: break-word;
    flex: 1;
    min-width: 0;
    line-height: 1.35;
  }
  .ai-prompt-chip-placeholder {
    color: rgba(167, 243, 208, 0.85);
    font-style: italic;
  }
  .ai-prompt-chip-close {
    margin-left: 6px;
    margin-top: 2px;
    cursor: pointer;
    color: rgba(134, 239, 172, 0.65);
    font-size: 14px;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
  }
  .ai-prompt-chip-close:hover { color: #f87171; }
`;

const escapeHtml = (s) => {
  if (s == null || s === '') return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
};

/** Same sparkle + pencil motif as PolishIcon, inlined for contenteditable HTML. */
/**
 * Zero-width space after non-editable chips so the browser has a real text offset to type at.
 * Stripped in htmlToValue so it never persists in the saved draft.
 */
const EDITOR_CARET_ZWSP = '\u200B';

const AI_PROMPT_CHIP_ICON_HTML =
  '<span class="ai-prompt-chip-icon">' +
  '<svg class="ai-prompt-chip-svg" width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
  '<path d="M4.5 2L4.5 4.8L8.5 5.5L5.5 6.2L4.5 9L3.5 6.2L0.5 5.5L3.5 4.8Z" fill="currentColor"/>' +
  '<path d="M9 17L15.5 10.5L17.5 12.5L11 19H9V17Z" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>' +
  '<path d="M15.5 10.5L17 8.5L19.5 11L17.5 12.5Z" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>' +
  '</svg></span>';

function varKeyToChipHtml(content) {
  if (content === 'CLEAN_PENDING') {
    return (
      `<span contenteditable="false" data-variable="${content}" class="variable-chip">` +
      `<span class="chip-close" data-remove="${content}">×</span>` +
      `<span class="chip-icon"><svg stroke="currentColor" fill="none" stroke-width="2" viewBox="0 0 24 24" height="14" width="14" xmlns="http://www.w3.org/2000/svg"><path d="m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.9-9.9c1-1 2.5-1 3.4 0l4.3 4.3c1 1 1 2.5 0 3.4L10.5 21z"></path><path d="m14 7 3 3"></path><path d="M5 11 11 5"></path></svg></span>` +
      `<span class="chip-text">"Clean variable"</span></span>`
    );
  }
  if (content.startsWith('CLEAN|')) {
    const [, column, , , fallback] = content.split('|');
    return (
      `<span contenteditable="false" data-variable="${content}" class="variable-chip">` +
      `<span class="chip-close" data-remove="${content}">×</span>` +
      `<span class="chip-icon"><svg stroke="currentColor" fill="none" stroke-width="2" viewBox="0 0 24 24" height="14" width="14" xmlns="http://www.w3.org/2000/svg"><path d="m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.9-9.9c1-1 2.5-1 3.4 0l4.3 4.3c1 1 1 2.5 0 3.4L10.5 21z"></path><path d="m14 7 3 3"></path><path d="M5 11 11 5"></path></svg></span>` +
      `<span class="chip-text"><span class="chip-nested-label">${column || 'field'}</span> or '${escapeHtml(fallback)}'</span></span>`
    );
  }
  const varDef = AVAILABLE_VARIABLES.find((v) => v.key === content);
  const label = varDef?.label || content;
  return (
    `<span contenteditable="false" data-variable="${content}" class="variable-chip">` +
    `<span class="chip-icon">T</span><span class="chip-text">${escapeHtml(label)}</span><span class="chip-close" style="margin-right: 0; margin-left: 6px;" data-remove="${content}">×</span></span>`
  );
}

/**
 * Next index of `//` that starts an AI delimiter, not the slashes in `http://` / `https://`.
 */
function indexOfPromptDelimiter(str, fromIndex = 0) {
  let i = fromIndex;
  while (i < str.length) {
    const j = str.indexOf('//', i);
    if (j === -1) return -1;
    if (j > 0 && str[j - 1] === ':') {
      i = j + 2;
      continue;
    }
    return j;
  }
  return -1;
}

/** True if the model uses AI `//` delimiters (ignores `://` in URLs). */
function modelHasAiDelimiter(str) {
  return typeof str === 'string' && indexOfPromptDelimiter(str, 0) !== -1;
}

/**
 * Split draft text into plain text, {{variable}}, and // AI prompt // segments.
 * Unclosed // starts an open AI segment through end of string (matches backend DOTALL intent).
 */
function parseDraftSegments(str, includeAi) {
  const segs = [];
  let i = 0;
  while (i < str.length) {
    const iVar = str.indexOf('{{', i);
    const iAi = includeAi ? indexOfPromptDelimiter(str, i) : -1;
    let nextIdx = Infinity;
    let kind = null;
    if (iVar !== -1 && iVar < nextIdx) {
      nextIdx = iVar;
      kind = 'var';
    }
    if (iAi !== -1 && iAi < nextIdx) {
      nextIdx = iAi;
      kind = 'ai';
    }
    if (kind === null) {
      segs.push({ type: 'text', content: str.slice(i) });
      break;
    }
    if (nextIdx > i) {
      segs.push({ type: 'text', content: str.slice(i, nextIdx) });
    }
    if (kind === 'var') {
      const end = str.indexOf('}}', nextIdx + 2);
      if (end === -1) {
        segs.push({ type: 'text', content: str.slice(nextIdx) });
        break;
      }
      segs.push({ type: 'var', content: str.slice(nextIdx + 2, end) });
      i = end + 2;
      continue;
    }
    if (kind === 'ai') {
      const after = nextIdx + 2;
      const close = indexOfPromptDelimiter(str, after);
      if (close === -1) {
        segs.push({ type: 'ai', content: str.slice(after), closed: false });
        break;
      }
      segs.push({ type: 'ai', content: str.slice(after, close), closed: true });
      i = close + 2;
      continue;
    }
  }
  return segs;
}

/** Closed //…// segments only; open segments render as plain text in valueToHtml. */
function closedAiPromptChipHtml(inner) {
  const enc = encodeURIComponent(inner);
  const hasText = (inner || '').trim().length > 0;
  // Use explicit <br> (same as open segments) so innerHTML matches valueToHtml output after
  // chipify — raw newlines inside the span normalize differently in browsers and caused
  // innerHTML !== nextHtml on every input, nuking the caret for multi-line prompts.
  const displayInner = hasText
    ? escapeHtml(inner).replace(/\n/g, '<br>')
    : '<span class="ai-prompt-chip-placeholder">empty</span>';
  return (
    `<span contenteditable="false" class="ai-prompt-chip" data-ai-inner="${enc}" data-ai-closed="1">` +
    AI_PROMPT_CHIP_ICON_HTML +
    `<span class="ai-prompt-chip-text">${displayInner}</span>` +
    '<span class="ai-prompt-chip-close" data-ai-remove="1">×</span>' +
    `</span>${EDITOR_CARET_ZWSP}`
  );
}

/** Model length of a text node (ZWSP sentinels do not count). */
function textNodeModelLength(raw) {
  return (raw || '').replace(/\u200B/g, '').length;
}

/**
 * Map a model offset inside this text node to a DOM offset (ZWSP skipped for index math).
 * `modelIndexWithinNode` is 0..modelLen inclusive (inclusive end = caret after last char).
 */
function rawOffsetForModelIndexInTextNode(raw, modelIndexWithinNode) {
  const s = raw || '';
  if (modelIndexWithinNode <= 0) return 0;
  let m = 0;
  for (let i = 0; i < s.length; i++) {
    if (s[i] === EDITOR_CARET_ZWSP) continue;
    m += 1;
    if (m === modelIndexWithinNode) return i + 1;
  }
  return s.length;
}

const EDITOR_BLOCK_TAGS = new Set(['DIV', 'P', 'LI', 'BLOCKQUOTE', 'PRE']);

function editorElementToModelToken(el) {
  if (!el?.classList) return null;
  if (el.classList.contains('variable-chip')) {
    const k = el.getAttribute('data-variable');
    return k ? `{{${k}}}` : null;
  }
  if (el.classList.contains('ai-prompt-chip')) {
    const enc = el.getAttribute('data-ai-inner') || '';
    let inner = '';
    try {
      inner = decodeURIComponent(enc);
    } catch {
      inner = '';
    }
    const closed = el.getAttribute('data-ai-closed') === '1';
    return `//${inner}${closed ? '//' : ''}`;
  }
  return null;
}

/** Model-string offset of the caret; must stay aligned with htmlToValue(editor.innerHTML). */
function getModelOffsetBeforeCaret(root, range, htmlToValue) {
  if (!root || !range || !root.contains(range.startContainer)) return 0;
  try {
    const pre = range.cloneRange();
    pre.selectNodeContents(root);
    pre.setEnd(range.startContainer, range.startOffset);
    const wrap = document.createElement('div');
    wrap.appendChild(pre.cloneContents());
    return htmlToValue(wrap.innerHTML).length;
  } catch {
    return 0;
  }
}

function applySelectionRange(range) {
  const sel = window.getSelection();
  if (!sel) return;
  sel.removeAllRanges();
  sel.addRange(range);
}

/** Prefer caret inside the ZWSP text node after a chip (stable typing); else after the element. */
function placeCaretAfterNonEditableChip(chipEl) {
  const next = chipEl.nextSibling;
  if (next && next.nodeType === Node.TEXT_NODE) {
    const nv = next.nodeValue || '';
    const zi = nv.indexOf(EDITOR_CARET_ZWSP);
    if (zi !== -1) {
      const r = document.createRange();
      r.setStart(next, zi);
      r.collapse(true);
      applySelectionRange(r);
      return;
    }
  }
  const r = document.createRange();
  r.setStartAfter(chipEl);
  r.collapse(true);
  applySelectionRange(r);
}

/**
 * Place collapsed caret after `targetOffset` model chars (same indexing as htmlToValue on root).
 */
function setCaretToModelOffset(root, targetOffset, htmlToValue) {
  if (!root) return;
  const max = htmlToValue(root.innerHTML).length;
  const tgt = Math.min(Math.max(0, targetOffset), max);
  let pos = 0;

  const walk = (node, isRoot) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const raw = node.nodeValue || '';
      const mlen = textNodeModelLength(raw);
      if (mlen > 0 && tgt >= pos && tgt <= pos + mlen) {
        const local = tgt - pos;
        const off = rawOffsetForModelIndexInTextNode(raw, local);
        const r = document.createRange();
        r.setStart(node, off);
        r.collapse(true);
        applySelectionRange(r);
        return true;
      }
      if (mlen === 0 && raw.length > 0 && tgt === pos) {
        const r = document.createRange();
        r.setStart(node, 0);
        r.collapse(true);
        applySelectionRange(r);
        return true;
      }
      pos += mlen;
      return false;
    }
    if (node.nodeName === 'BR') {
      if (tgt === pos) {
        const r = document.createRange();
        r.setStartBefore(node);
        r.collapse(true);
        applySelectionRange(r);
        return true;
      }
      pos += 1;
      return false;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tok = editorElementToModelToken(node);
      if (tok !== null) {
        const len = tok.length;
        if (tgt <= pos) {
          const r = document.createRange();
          r.setStartBefore(node);
          r.collapse(true);
          applySelectionRange(r);
          return true;
        }
        if (tgt <= pos + len) {
          placeCaretAfterNonEditableChip(node);
          return true;
        }
        pos += len;
        return false;
      }
    }

    const childNodes = Array.from(node.childNodes);
    const isBlock = !isRoot && node.nodeType === Node.ELEMENT_NODE && EDITOR_BLOCK_TAGS.has(node.nodeName);

    if (isBlock) {
      const prev = node.previousSibling;
      const prevIsBlock = prev && prev.nodeType === Node.ELEMENT_NODE && EDITOR_BLOCK_TAGS.has(prev.nodeName);
      const leadingNl = prev && !prevIsBlock ? 1 : 0;

      if (leadingNl) {
        if (tgt === pos) {
          const r = document.createRange();
          r.setStartBefore(node);
          r.collapse(true);
          applySelectionRange(r);
          return true;
        }
        pos += 1;
      }

      if (childNodes.length === 1 && childNodes[0].nodeName === 'BR') {
        if (tgt === pos) {
          const r = document.createRange();
          r.setStartBefore(childNodes[0]);
          r.collapse(true);
          applySelectionRange(r);
          return true;
        }
        pos += 1;
        if (tgt === pos) {
          const r = document.createRange();
          r.setStartAfter(node);
          r.collapse(true);
          applySelectionRange(r);
          return true;
        }
        return false;
      }

      for (const c of childNodes) {
        if (walk(c, false)) return true;
      }

      if (tgt === pos) {
        const last = childNodes[childNodes.length - 1];
        const r = document.createRange();
        if (last) {
          if (last.nodeType === Node.TEXT_NODE) {
            r.setStart(last, (last.nodeValue || '').length);
          } else {
            r.setStartAfter(last);
          }
        } else {
          r.setStart(node, 0);
        }
        r.collapse(true);
        applySelectionRange(r);
        return true;
      }
      pos += 1;
      if (tgt === pos) {
        const r = document.createRange();
        r.setStartAfter(node);
        r.collapse(true);
        applySelectionRange(r);
        return true;
      }
      return false;
    }

    for (const c of childNodes) {
      if (walk(c, false)) return true;
    }
    return false;
  };

  if (!walk(root, true)) {
    const r = document.createRange();
    r.selectNodeContents(root);
    r.collapse(false);
    applySelectionRange(r);
  }
}

const VariableChipInput = ({ 
  value, 
  onChange, 
  placeholder, 
  isTextarea = false,
  minH = "auto",
  hideHint = false,
  enableAiPromptChips = false,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showAiPromptHint, setShowAiPromptHint] = useState(false);
  const [isConfiguringClean, setIsConfiguringClean] = useState(false);
  const [showNestedMenu, setShowNestedMenu] = useState(false);
  
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [aiHintPosition, setAiHintPosition] = useState({ top: 0, left: 0 });
  const [nestedMenuPosition, setNestedMenuPosition] = useState({ top: 0, left: 0 });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [nestedSearchTerm, setNestedSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [nestedSelectedIndex, setNestedSelectedIndex] = useState(0);

  const [fallbackError, setFallbackError] = useState(false);

  const [columnConfirmed, setColumnConfirmed] = useState(false);
  const [cleanConfig, setCleanConfig] = useState({
    column: '',
    format: 'None',
    removeEmojis: false,
    fallback: ''
  });
  
  const editorRef = useRef(null);
  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const aiPromptHintRef = useRef(null);
  const configRef = useRef(null);
  const nestedMenuRef = useRef(null);
  // Initialize to null (not value) so first useEffect run will populate the DOM
  const lastValueRef = useRef(null);
  const isInternalChange = useRef(false);

  const filteredVariables = AVAILABLE_VARIABLES.filter(v => 
    v.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredNestedVariables = AVAILABLE_VARIABLES.filter(v => 
    v.label.toLowerCase().includes(nestedSearchTerm.toLowerCase())
  );
  
  const showCleanAction = "clean variable".includes(searchTerm.toLowerCase());
  const menuItemsCount = filteredVariables.length + (showCleanAction ? 1 : 0);

  useEffect(() => {
    setSelectedIndex(0);
  }, [searchTerm, showMenu]);

  useEffect(() => {
    setNestedSelectedIndex(0);
  }, [nestedSearchTerm, showNestedMenu]);

  const valueToHtml = useCallback(
    (val) => {
      if (!val) return '';
      if (enableAiPromptChips) {
        const segs = parseDraftSegments(val, true);
        return segs
          .map((seg) => {
            if (seg.type === 'text') return escapeHtml(seg.content).replace(/\n/g, '<br>');
            if (seg.type === 'var') return varKeyToChipHtml(seg.content);
            if (seg.type === 'ai') {
              if (seg.closed) {
                return closedAiPromptChipHtml(seg.content);
              }
              // Unclosed //… (prompt still being written): plain editable text so the whole body
              // is not one non-editable chip until the closing // is typed.
              return `//${escapeHtml(seg.content).replace(/\n/g, '<br>')}`;
            }
            return '';
          })
          .join('');
      }
      const withChips = val.replace(/\{\{(.+?)\}\}/g, (match, content) => varKeyToChipHtml(content));
      return withChips.replace(/\n/g, '<br>');
    },
    [enableAiPromptChips]
  );

  const htmlToValue = useCallback((html) => {
    if (!html) return '';
    const temp = document.createElement('div');
    temp.innerHTML = html;

    temp.querySelectorAll('.ai-prompt-chip').forEach((chip) => {
      const enc = chip.getAttribute('data-ai-inner') || '';
      let inner = '';
      try {
        inner = decodeURIComponent(enc);
      } catch {
        inner = '';
      }
      const closed = chip.getAttribute('data-ai-closed') === '1';
      chip.replaceWith(document.createTextNode(`//${inner}${closed ? '//' : ''}`));
    });

    // Replace variable chips with template tokens before walking
    temp.querySelectorAll('.variable-chip').forEach(chip => {
      const varKey = chip.getAttribute('data-variable');
      if (varKey) chip.replaceWith(`{{${varKey}}}`);
    });

    // Chrome's contentEditable wraps each line in <div> and represents blank lines as
    // <div><br></div>. innerText on a detached element loses all structure entirely.
    //
    // Walk the DOM tree to reconstruct the exact text with newlines:
    //   - text node:  emit its value as-is
    //   - <br>:       emit \n
    //   - block elem: emit leading \n if preceded by text/inline (closes that line),
    //                 then children, then trailing \n (ends this block's line)
    //   - <div><br></div>: Chrome's empty-line anchor — <br> is only for cursor
    //                      visibility, so emit leading \n (if needed) + \n for blank line
    const BLOCK_TAGS = new Set(['DIV', 'P', 'LI', 'BLOCKQUOTE', 'PRE']);

    const walk = (node, isRoot = false) => {
      if (node.nodeType === Node.TEXT_NODE) {
        return (node.nodeValue || '').replace(/\u200B/g, '');
      }
      if (node.nodeName === 'BR') return '\n';

      const childNodes = Array.from(node.childNodes);
      const isBlock = !isRoot && BLOCK_TAGS.has(node.nodeName);

      if (isBlock) {
        const prev = node.previousSibling;
        const prevIsBlock = prev && prev.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has(prev.nodeName);
        // When preceded by a text/inline node, that node has no trailing \n — add one to close it
        const leadingNl = (prev && !prevIsBlock) ? '\n' : '';

        // <div><br></div>: empty-line placeholder; the <br> is not a real line break
        if (childNodes.length === 1 && childNodes[0].nodeName === 'BR') {
          return leadingNl + '\n';
        }

        return leadingNl + childNodes.map(c => walk(c)).join('') + '\n';
      }

      return childNodes.map(c => walk(c)).join('');
    };

    return walk(temp, true).replace(/\n+$/, '');
  }, []);

  const chipifyAiSectionsLive = useCallback(
    (modelStr) => {
      const el = editorRef.current;
      if (!el || document.activeElement !== el) return;
      if (!modelHasAiDelimiter(modelStr)) return;
      const nextHtml = valueToHtml(modelStr);
      if (el.innerHTML === nextHtml) return;

      const closedAiCount = parseDraftSegments(modelStr, true).filter((s) => s.type === 'ai' && s.closed).length;
      if (
        closedAiCount > 0 &&
        closedAiCount === el.querySelectorAll('.ai-prompt-chip').length &&
        htmlToValue(el.innerHTML) === modelStr
      ) {
        return;
      }

      const sel = window.getSelection();
      let off = 0;
      if (sel?.rangeCount && el.contains(sel.getRangeAt(0).startContainer)) {
        off = getModelOffsetBeforeCaret(el, sel.getRangeAt(0), htmlToValue);
      }

      el.innerHTML = nextHtml;
      lastValueRef.current = modelStr;
      setCaretToModelOffset(el, off, htmlToValue);
    },
    [htmlToValue, valueToHtml]
  );

  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    if (editorRef.current && value !== lastValueRef.current) {
      editorRef.current.innerHTML = valueToHtml(value || '');
      lastValueRef.current = value;
    }
  }, [value, valueToHtml]);

  // Catch-up when focus leaves (e.g. tab away) — usually live chipify already updated the DOM.
  const handleEditorBlur = useCallback(() => {
    setShowAiPromptHint(false);
    if (!enableAiPromptChips) return;
    window.setTimeout(() => {
      if (!editorRef.current) return;
      const ae = document.activeElement;
      if (menuRef.current?.contains(ae)) return;
      if (aiPromptHintRef.current?.contains(ae)) return;
      if (configRef.current?.contains(ae)) return;
      if (nestedMenuRef.current?.contains(ae)) return;

      const latest = htmlToValue(editorRef.current.innerHTML);
      if (!modelHasAiDelimiter(latest)) return;

      const nextHtml = valueToHtml(latest);
      if (editorRef.current.innerHTML === nextHtml) return;

      const closedAi = parseDraftSegments(latest, true).filter((s) => s.type === 'ai' && s.closed).length;
      if (closedAi > 0 && closedAi === editorRef.current.querySelectorAll('.ai-prompt-chip').length) {
        return;
      }

      editorRef.current.innerHTML = nextHtml;
      lastValueRef.current = latest;
      if (latest !== value) {
        isInternalChange.current = true;
        onChange(latest);
      }
    }, 0);
  }, [enableAiPromptChips, htmlToValue, valueToHtml, value, onChange]);

  const checkSearchContext = useCallback(() => {
    if (!editorRef.current || isConfiguringClean) return;
    
    const html = editorRef.current.innerHTML;
    let newValue = htmlToValue(html);
    
    if (!newValue || newValue.trim() === '') {
      if (editorRef.current.innerHTML !== '') {
        editorRef.current.innerHTML = '';
      }
      newValue = '';
    }

    // Mask completed {{...}} pairs so lastIndexOf only finds the user's unclosed {{.
    // Same-length replacement preserves string positions for slicing from newValue.
    let masked = newValue.replace(/\{\{.+?\}\}/g, (m) => '\x01'.repeat(m.length));
    if (enableAiPromptChips) {
      masked = masked.replace(/(?<!:)\/\/[\s\S]*?(?<!:)\/\//g, (m) => '\x01'.repeat(m.length));
    }
    const lastOpenIndex = masked.lastIndexOf('{{');

    let shouldShowVariableMenu = false;
    let varSearchSlice = '';
    if (lastOpenIndex !== -1) {
      const afterOpen = newValue.slice(lastOpenIndex + 2);
      const nextNewline = afterOpen.indexOf('\n');
      const textAfterOpen = nextNewline !== -1 ? afterOpen.slice(0, nextNewline) : afterOpen;
      varSearchSlice = textAfterOpen;
      if (!textAfterOpen.includes(' ') && !textAfterOpen.includes('}}')) {
        shouldShowVariableMenu = true;
      }
    }

    if (shouldShowVariableMenu) {
      setSearchTerm(varSearchSlice);
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        const menuWidth = 220;
        const padding = 16;
        let calculatedLeft = rect.left;
        if (calculatedLeft + menuWidth > window.innerWidth - padding) {
          calculatedLeft = window.innerWidth - menuWidth - padding;
        }
        setMenuPosition({ top: rect.bottom + 4, left: Math.max(padding, calculatedLeft) });
      }
      setShowMenu(true);
    } else {
      setShowMenu(false);
    }

    if (enableAiPromptChips && !shouldShowVariableMenu) {
      const segs = parseDraftSegments(newValue, true);
      const lastSeg = segs[segs.length - 1];
      if (lastSeg?.type === 'ai' && !lastSeg.closed) {
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0 && editorRef.current?.contains(selection.getRangeAt(0).startContainer)) {
          const range = selection.getRangeAt(0);
          const rect = range.getBoundingClientRect();
          const panelW = 268;
          const padding = 16;
          let left = rect.left;
          if (left + panelW > window.innerWidth - padding) {
            left = window.innerWidth - panelW - padding;
          }
          setAiHintPosition({ top: rect.bottom + 4, left: Math.max(padding, left) });
        }
        setShowAiPromptHint(true);
      } else {
        setShowAiPromptHint(false);
      }
    } else {
      setShowAiPromptHint(false);
    }

    lastValueRef.current = newValue;
    isInternalChange.current = true;
    onChange(newValue);

    if (enableAiPromptChips && modelHasAiDelimiter(newValue)) {
      chipifyAiSectionsLive(newValue);
    }
  }, [onChange, htmlToValue, isConfiguringClean, enableAiPromptChips, chipifyAiSectionsLive]);

  const insertTag = useCallback((tagKey, isPending = false) => {
    if (!editorRef.current) return;
    const currentValue = lastValueRef.current || '';
    let masked = currentValue.replace(/\{\{.+?\}\}/g, (m) => '\x01'.repeat(m.length));
    if (enableAiPromptChips) {
      masked = masked.replace(/(?<!:)\/\/[\s\S]*?(?<!:)\/\//g, (m) => '\x01'.repeat(m.length));
    }
    const lastOpenIndex = masked.lastIndexOf('{{');
    
    let newValue;
    if (lastOpenIndex !== -1) {
      const afterOpen = currentValue.slice(lastOpenIndex + 2);
      const remainder = afterOpen.split(/[\s}]/)[0] || '';
      newValue = currentValue.slice(0, lastOpenIndex) + `{{${tagKey}}}` + currentValue.slice(lastOpenIndex + 2 + remainder.length);
    } else {
      newValue = currentValue + `{{${tagKey}}}`;
    }
    
    isInternalChange.current = false; 
    onChange(newValue);
    
    if (!isPending) {
        setShowMenu(false);
        setIsConfiguringClean(false);
        setTimeout(() => {
          editorRef.current?.focus();
          const range = document.createRange();
          const sel = window.getSelection();
          range.selectNodeContents(editorRef.current);
          range.collapse(false);
          sel?.removeAllRanges();
          sel?.addRange(range);
        }, 10);
    }
  }, [onChange, enableAiPromptChips]);

  const resetCleanState = () => {
    setCleanConfig({ column: '', format: 'None', removeEmojis: false, fallback: '' });
    setColumnConfirmed(false);
    setNestedSearchTerm('');
    setShowNestedMenu(false);
    setFallbackError(false);
  };

  const handleSaveCleanVar = () => {
    if (!cleanConfig.fallback.trim()) {
      setFallbackError(true);
      return;
    }
    const finalTag = `CLEAN|${cleanConfig.column || 'field'}|${cleanConfig.format}|${cleanConfig.removeEmojis}|${cleanConfig.fallback}`;
    const newValue = lastValueRef.current.replace('{{CLEAN_PENDING}}', `{{${finalTag}}}`);
    isInternalChange.current = false;
    onChange(newValue);
    setIsConfiguringClean(false);
    resetCleanState();
  };

  const cancelCleanVar = useCallback(() => {
    const newValue = (lastValueRef.current || '').replace('{{CLEAN_PENDING}}', '');
    isInternalChange.current = false;
    onChange(newValue);
    setIsConfiguringClean(false);
    resetCleanState();
  }, [onChange]);

  const selectNestedVariable = (variable) => {
    setCleanConfig(prev => ({ ...prev, column: variable.label }));
    setColumnConfirmed(true);
    setShowNestedMenu(false);
    setNestedSearchTerm('');
  };

  const handleColumnInput = (e) => {
    const val = e.target.value;
    setCleanConfig(prev => ({ ...prev, column: val }));
    if (val.includes('{{')) {
      if (configRef.current) {
        const inputRect = e.target.getBoundingClientRect();
        const panelRect = configRef.current.getBoundingClientRect();
        
        const nestedWidth = 220;
        let calculatedLeft = panelRect.left - nestedWidth - 10;
        
        if (calculatedLeft < 10) {
            calculatedLeft = panelRect.right + 10;
        }

        setNestedMenuPosition({ top: inputRect.top, left: calculatedLeft });
        setShowNestedMenu(true);
        const lastOpenIndex = val.lastIndexOf('{{');
        setNestedSearchTerm(lastOpenIndex !== -1 ? val.slice(lastOpenIndex + 2) : '');
      }
    } else if (showNestedMenu) {
      const lastOpenIndex = val.lastIndexOf('{{');
      if (lastOpenIndex !== -1) {
        setNestedSearchTerm(val.slice(lastOpenIndex + 2));
      } else {
        setShowNestedMenu(false);
      }
    }
  };

  const handleNestedKeyDown = (e) => {
    if (showNestedMenu && filteredNestedVariables.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setNestedSelectedIndex(prev => (prev + 1) % filteredNestedVariables.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setNestedSelectedIndex(prev => (prev - 1 + filteredNestedVariables.length) % filteredNestedVariables.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        selectNestedVariable(filteredNestedVariables[nestedSelectedIndex]);
      } else if (e.key === 'Escape') {
        setShowNestedMenu(false);
      }
    }
  };

  const handleEditorKeyDown = (e) => {
    if (showMenu && menuItemsCount > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % menuItemsCount);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + menuItemsCount) % menuItemsCount);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const isCleanSelected = showCleanAction && selectedIndex === 0;
        if (isCleanSelected) {
          // INSERT PENDING CHIP FIRST
          insertTag('CLEAN_PENDING', true);
          setIsConfiguringClean(true);
          setShowMenu(false);
          
          // PREMIUM FIX: Find actual chip position to anchor dropdown exactly below it
          setTimeout(() => {
            const pendingChip = editorRef.current?.querySelector('[data-variable="CLEAN_PENDING"]');
            if (pendingChip) {
              const chipRect = pendingChip.getBoundingClientRect();
              const configWidth = 320;
              const padding = 16;
              let configLeft = chipRect.left;
              
              if (configLeft + configWidth > window.innerWidth - padding) {
                configLeft = window.innerWidth - configWidth - padding;
              }

              setMenuPosition({ 
                top: chipRect.bottom + 8, // Force exactly below the chip line
                left: Math.max(padding, configLeft) 
              });
            }
          }, 0);
        } else {
          const varIdx = showCleanAction ? selectedIndex - 1 : selectedIndex;
          insertTag(filteredVariables[varIdx].key);
        }
      }
    }
    if (e.key === 'Escape') {
      if (isConfiguringClean) cancelCleanVar();
      setShowMenu(false);
      setShowAiPromptHint(false);
    }
  };

  const handleClick = useCallback((e) => {
    const aiCloseBtn = e.target.closest('.ai-prompt-chip-close');
    if (aiCloseBtn) {
      e.preventDefault();
      e.stopPropagation();
      const chip = aiCloseBtn.closest('.ai-prompt-chip');
      chip?.remove();
      if (editorRef.current) {
        const newValue = htmlToValue(editorRef.current.innerHTML);
        lastValueRef.current = newValue;
        isInternalChange.current = true;
        onChange(newValue);
      }
      return;
    }
    const aiChipEdit = e.target.closest('.ai-prompt-chip');
    if (aiChipEdit && !e.target.closest('.ai-prompt-chip-close')) {
      e.preventDefault();
      e.stopPropagation();
      if (!editorRef.current) return;
      const enc = aiChipEdit.getAttribute('data-ai-inner') || '';
      let inner = '';
      try {
        inner = decodeURIComponent(enc);
      } catch {
        inner = '';
      }
      // Drop closing // so the segment is "open" again: user edits freely, then types // to finish.
      const plain = `//${inner}`;

      const rBefore = document.createRange();
      rBefore.setStartBefore(aiChipEdit);
      rBefore.collapse(true);
      const chipStart = getModelOffsetBeforeCaret(editorRef.current, rBefore, htmlToValue);

      aiChipEdit.replaceWith(document.createTextNode(plain));

      const newValue = htmlToValue(editorRef.current.innerHTML);
      lastValueRef.current = newValue;
      isInternalChange.current = true;
      onChange(newValue);

      requestAnimationFrame(() => {
        if (!editorRef.current) return;
        setCaretToModelOffset(editorRef.current, chipStart + 2 + inner.length, htmlToValue);
        editorRef.current.focus();
      });
      return;
    }
    const closeBtn = e.target.closest('.chip-close');
    if (closeBtn) {
      e.preventDefault(); e.stopPropagation();
      const chip = closeBtn.closest('.variable-chip');
      if (chip) {
        const varKey = chip.getAttribute('data-variable');
        chip.remove();
        if (editorRef.current) {
          const newValue = htmlToValue(editorRef.current.innerHTML);
          lastValueRef.current = newValue;
          isInternalChange.current = true;
          onChange(newValue);
          if (varKey === 'CLEAN_PENDING') {
            setIsConfiguringClean(false);
            resetCleanState();
          }
        }
      }
    } else {
      checkSearchContext();
    }
  }, [htmlToValue, onChange, checkSearchContext]);

  // Clamp config panel inside viewport after it renders.
  // useLayoutEffect with only isConfiguringClean as dep so it runs once on open
  // and never loops. clientWidth excludes the browser scrollbar (window.innerWidth doesn't).
  useLayoutEffect(() => {
    if (!isConfiguringClean) return;
    const raf = requestAnimationFrame(() => {
      if (!configRef.current) return;
      const rect = configRef.current.getBoundingClientRect();
      const availW = document.documentElement.clientWidth;
      const availH = document.documentElement.clientHeight;
      const padding = 16;
      setMenuPosition(prev => {
        let { top, left } = prev;
        if (rect.right > availW - padding) left = availW - rect.width - padding;
        if (rect.bottom > availH - padding) top = availH - rect.height - padding;
        return { top: Math.max(padding, top), left: Math.max(padding, left) };
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [isConfiguringClean]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isConfiguringClean) {
        const isInsideConfig = configRef.current && configRef.current.contains(event.target);
        const isInsideNested = nestedMenuRef.current && nestedMenuRef.current.contains(event.target);
        if (!isInsideConfig && !isInsideNested) cancelCleanVar();
      }
      if (showMenu) {
        const isInsideMenu = menuRef.current && menuRef.current.contains(event.target);
        const isInsideContainer = containerRef.current && containerRef.current.contains(event.target);
        if (!isInsideMenu && !isInsideContainer) setShowMenu(false);
      }
      if (showAiPromptHint) {
        const inHint = aiPromptHintRef.current?.contains(event.target);
        const inContainer = containerRef.current?.contains(event.target);
        if (!inHint && !inContainer) setShowAiPromptHint(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu, showAiPromptHint, isConfiguringClean, cancelCleanVar]);

  return (
    <Box position="relative" ref={containerRef}>
      <style>{chipStyles}</style>
      <Box
        bg="#111111"
        border="1px solid"
        borderColor="rgba(255,255,255,0.12)"
        borderRadius="lg"
        p={3}
        minH={minH}
        cursor="text"
        onClick={() => editorRef.current?.focus()}
        _focusWithin={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
      >
        <Box
          ref={editorRef} contentEditable suppressContentEditableWarning
          onInput={checkSearchContext} onKeyDown={handleEditorKeyDown}
          onBlur={handleEditorBlur}
          onKeyUp={(e) => ['ArrowLeft', 'ArrowRight'].includes(e.key) && checkSearchContext()}
          onClick={handleClick} color="#fafafa" fontSize="sm" minH={isTextarea ? minH : "auto"}
          outline="none" whiteSpace="pre-wrap" wordBreak="break-word"
          sx={{ 
            '&:empty:before': { 
              content: `"${placeholder}"`, 
              color: 'rgba(255,255,255,0.3)',
              pointerEvents: 'none'
            } 
          }}
        />
        {!hideHint && (
          <Text color="rgba(255,255,255,0.3)" fontSize="xs" mt={2}>
            Type {'{{...}}'} for variables, type {'//...//'} for AI prompt injection.
          </Text>
        )}
      </Box>

      {showMenu && (
        <Portal>
          <Box
            ref={menuRef}
            position="fixed"
            top={`${menuPosition.top}px`}
            left={`${menuPosition.left}px`}
            bg="#0f0f0f"
            border="1px solid"
            borderColor="rgba(255,255,255,0.10)"
            borderRadius="lg"
            zIndex={2000}
            minW="180px"
            maxH="240px"
            overflowY="auto"
            py={1}
            px={1}
            boxShadow="0 4px 24px rgba(0,0,0,0.5)"
            sx={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {showCleanAction && (
              <Box
                mx={0}
                px={1.5}
                py={1.5}
                cursor="pointer"
                borderRadius="md"
                bg={selectedIndex === 0 ? 'rgba(255,255,255,0.08)' : 'transparent'}
                _hover={{ bg: 'rgba(255,255,255,0.06)' }}
                onClick={() => {
                  const padding = 16;
                  let configLeft = menuPosition.left;
                  if (configLeft + 320 > window.innerWidth - padding) {
                    configLeft = window.innerWidth - 320 - padding;
                  }
                  setMenuPosition(prev => ({ ...prev, left: Math.max(padding, configLeft) }));
                  insertTag('CLEAN_PENDING', true);
                  setIsConfiguringClean(true);
                  setShowMenu(false);
                }}
              >
                <HStack color="rgba(255,255,255,0.6)" spacing={2}>
                  <LuEraser size={14} />
                  <Text fontSize="sm">Clean variable</Text>
                </HStack>
              </Box>
            )}
            <Box px={1.5} py={1}>
              <Text color="rgba(255,255,255,0.3)" fontSize="2xs" fontWeight="medium" textTransform="uppercase" letterSpacing="wider" sx={{ fontSize: '10px' }}>
                Variables
              </Text>
            </Box>
            {filteredVariables.map((variable, index) => {
              const actualIdx = showCleanAction ? index + 1 : index;
              return (
                <Box
                  key={variable.key}
                  mx={0}
                  px={1.5}
                  py={1.5}
                  cursor="pointer"
                  borderRadius="md"
                  bg={actualIdx === selectedIndex ? 'rgba(255,255,255,0.08)' : 'transparent'}
                  _hover={{ bg: 'rgba(255,255,255,0.06)' }}
                  onClick={() => insertTag(variable.key)}
                >
                  <HStack spacing={2}>
                    <Box as={LuType} color="rgba(255,255,255,0.3)" fontSize="sm" />
                    <VStack align="start" spacing={0}>
                      <Text color="#fafafa" fontSize="sm">{variable.label}</Text>
                      <Text color="rgba(255,255,255,0.3)" fontSize="2xs">{`{{${variable.key}}}`}</Text>
                    </VStack>
                  </HStack>
                </Box>
              );
            })}
          </Box>
        </Portal>
      )}

      {showAiPromptHint && enableAiPromptChips && (
        <Portal>
          <Box
            ref={aiPromptHintRef}
            position="fixed"
            top={`${aiHintPosition.top}px`}
            left={`${aiHintPosition.left}px`}
            zIndex={1999}
            minW="248px"
            maxW="300px"
            bg="#0f0f0f"
            border="1px solid"
            borderColor="rgba(74, 222, 128, 0.3)"
            borderRadius="lg"
            boxShadow="0 4px 24px rgba(0,0,0,0.5)"
            overflow="hidden"
            onMouseDown={(e) => e.preventDefault()}
          >
            <HStack
              spacing={2}
              px={3}
              py={2}
              bg="rgba(34, 197, 94, 0.08)"
              borderBottom="1px solid"
              borderColor="rgba(74, 222, 128, 0.15)"
            >
              <Box as={LuSparkles} color="#4ade80" fontSize="16px" />
              <Text fontSize="sm" fontWeight="semibold" color="#4ade80">
                AI prompt
              </Text>
            </HStack>
            <VStack align="stretch" spacing={2} px={3} py={2.5}>
              <Text fontSize="2xs" fontWeight="bold" color="rgba(255,255,255,0.3)" textTransform="uppercase" letterSpacing="wider">
                Write an instruction
              </Text>
              <Text fontSize="sm" color="rgba(255,255,255,0.6)" lineHeight="short">
                Describe what the model should generate in this section. When you are finished, close with another{' '}
                <Text as="span" color="#4ade80" fontFamily="mono" fontSize="sm">
                  {'//'}
                </Text>
                {' '}— like ending a comment.
              </Text>
              <Text fontSize="2xs" color="rgba(255,255,255,0.3)" fontFamily="mono">
                {'// short personalized opener //'}
              </Text>
            </VStack>
          </Box>
        </Portal>
      )}

      {isConfiguringClean && (
        <Portal>
          <Box
            ref={configRef} position="fixed" top={`${menuPosition.top}px`} left={`${menuPosition.left}px`}
            bg="#0f0f0f" border="1px solid" borderColor="rgba(255,255,255,0.12)" borderRadius="lg"
            zIndex={2001} w="320px" boxShadow="2xl" p={4} color="#fafafa"
          >
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" fontWeight="bold">Clean variable</Text>
              <FormControl>
                <FormLabel fontSize="xs" color="#a3a3a3" mb={1}>Column to use if present</FormLabel>
                {columnConfirmed ? (
                  <Box
                    bg="#111111" border="1px solid" borderColor="rgba(255,255,255,0.12)"
                    borderRadius="md" px={2} h="32px" display="flex" alignItems="center"
                  >
                    <Box as="span" className="variable-chip" m={0} h="20px" bg="rgba(255, 255, 255, 0.12)">
                      <Box as="span" className="chip-icon" mr="4px">T</Box>
                      <Box as="span" className="chip-text" fontSize="12px">{cleanConfig.column}</Box>
                      <Box
                        as="span" className="chip-close" ml="6px" mr="0px"
                        onClick={() => { setCleanConfig(prev => ({ ...prev, column: '' })); setColumnConfirmed(false); }}
                      >
                        ×
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Input 
                    placeholder="{{first_name}}" size="sm" bg="#111111" border="1px solid rgba(255,255,255,0.12)"
                    color="#fafafa" _placeholder={{ color: 'rgba(255,255,255,0.25)' }}
                    value={cleanConfig.column} onChange={handleColumnInput} onKeyDown={handleNestedKeyDown}
                    _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }} autoFocus
                  />
                )}
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" color="#a3a3a3" mb={1}>Variable formatting</FormLabel>
                <Select 
                  size="sm" bg="#111111" border="1px solid rgba(255,255,255,0.12)" color="#fafafa" icon={<LuChevronDown />}
                  value={cleanConfig.format} onChange={(e) => setCleanConfig({...cleanConfig, format: e.target.value})}
                  _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
                >
                  <option value="None" style={{background: '#0f0f0f'}}>None</option>
                  <option value="Title Case" style={{background: '#0f0f0f'}}>Title Case</option>
                  <option value="lower case" style={{background: '#0f0f0f'}}>lower case</option>
                  <option value="Sentence case" style={{background: '#0f0f0f'}}>Sentence case</option>
                  <option value="UPPER CASE" style={{background: '#0f0f0f'}}>UPPER CASE</option>
                </Select>
              </FormControl>
              <Checkbox 
                size="sm" colorScheme="whiteAlpha"
                isChecked={cleanConfig.removeEmojis} onChange={(e) => setCleanConfig({...cleanConfig, removeEmojis: e.target.checked})}
              >
                <Text fontSize="xs" color="rgba(255,255,255,0.6)">Remove emojis and special characters</Text>
              </Checkbox>
              <FormControl isInvalid={fallbackError}>
                <FormLabel fontSize="xs" color="#a3a3a3" mb={0}>Fallback</FormLabel>
                <Text fontSize="10px" color="rgba(255,255,255,0.3)" mb={1}>Simple text to display if variable is empty</Text>
                <Input 
                  placeholder="Esteemed friend" size="sm" bg="#111111"
                  color="#fafafa" _placeholder={{ color: 'rgba(255,255,255,0.25)' }}
                  border="1px solid" 
                  borderColor={fallbackError ? "rgba(239,68,68,0.6)" : "rgba(255,255,255,0.12)"}
                  value={cleanConfig.fallback} 
                  onChange={(e) => {
                    setCleanConfig({...cleanConfig, fallback: e.target.value});
                    if (e.target.value.trim()) setFallbackError(false);
                  }}
                  _focus={{ borderColor: fallbackError ? 'rgba(239,68,68,0.8)' : 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
                />
                {fallbackError && (
                  <Text color="rgba(239,68,68,0.85)" fontSize="10px" mt={1}>
                    At least one character is required
                  </Text>
                )}
              </FormControl>
              <HStack spacing={2} pt={2}>
                <Button flex={1} variant="ghost" size="sm" color="#a3a3a3" borderRadius="full" _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }} _focus={{ boxShadow: 'none' }} onClick={cancelCleanVar}>Cancel</Button>
                <Button flex={1} bg="#fafafa" color="#0a0a0a" borderRadius="full" fontWeight="600" size="sm" _hover={{ opacity: 0.85 }} _focus={{ boxShadow: 'none' }} onClick={handleSaveCleanVar}>Save</Button>
              </HStack>
            </VStack>
          </Box>
        </Portal>
      )}

      {showNestedMenu && isConfiguringClean && (
        <Portal>
          <Box
            ref={nestedMenuRef} position="fixed" top={`${nestedMenuPosition.top}px`} left={`${nestedMenuPosition.left}px`}
            bg="#0f0f0f" border="1px solid rgba(255,255,255,0.10)" borderRadius="md"
            zIndex={2005} minW="220px" maxH="250px" overflowY="auto" boxShadow="xl"
            sx={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            <VStack spacing={0} align="stretch">
              <Box px={3} py={2} borderBottom="1px solid rgba(255,255,255,0.08)">
                <Text color="rgba(255,255,255,0.3)" fontSize="xs" fontWeight="bold">SELECT VARIABLE</Text>
              </Box>
              {filteredNestedVariables.map((variable, index) => (
                <Box
                  key={variable.key} px={3} py={2} cursor="pointer"
                  bg={index === nestedSelectedIndex ? 'rgba(255,255,255,0.08)' : 'transparent'}
                  _hover={{ bg: 'rgba(255,255,255,0.06)' }}
                  onClick={() => selectNestedVariable(variable)}
                >
                  <HStack>
                    <Box as={LuType} color="rgba(255,255,255,0.3)" fontSize="sm" />
                    <Text color="#fafafa" fontSize="sm">{variable.label}</Text>
                  </HStack>
                  <Text color="rgba(255,255,255,0.3)" fontSize="xs">{`{{${variable.key}}}`}</Text>
                </Box>
              ))}
            </VStack>
          </Box>
        </Portal>
      )}
    </Box>
  );
};

const MessageDraftingPanel = ({
  onClose,
  onSave,
  onPreview,
  campaignId,
  editingDraft = null,
}) => {
  const [typeOfMessage, setTypeOfMessage] = useState('email');
  const [emailName, setEmailName] = useState('New email');
  const [messageType, setMessageType] = useState('new_email');
  const [enableHtml, setEnableHtml] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewRows, setPreviewRows] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showPolishPanel, setShowPolishPanel] = useState(false);
  const [polishedBody, setPolishedBody] = useState(null);
  const [isPolishing, setIsPolishing] = useState(false);
  const [useAiDraft, setUseAiDraft] = useState(false);
  const [generatePrompt, setGeneratePrompt] = useState('');
  // 'idle' | 'generating' | 'ready' | 'approving'
  const [generatePhase, setGeneratePhase] = useState('idle');
  const [generateConfigId, setGenerateConfigId] = useState(null);
  const [generateStatus, setGenerateStatus] = useState(null);
  const [showSignaturesView, setShowSignaturesView] = useState(false);
  const [signatures, setSignatures] = useState([]);
  const [defaultForNewMessage, setDefaultForNewMessage] = useState('');
  const [defaultForFollowUps, setDefaultForFollowUps] = useState('');
  const appliedSigIdRef = useRef(null);
  const editingDraftIdRef = useRef(null);
  const [showAddSignatureModal, setShowAddSignatureModal] = useState(false);
  const [addSignatureName, setAddSignatureName] = useState('');
  const [addSignatureContent, setAddSignatureContent] = useState('');
  const [addSignatureDefaultForNewMessage, setAddSignatureDefaultForNewMessage] = useState(false);
  const [addSignatureDefaultForFollowUps, setAddSignatureDefaultForFollowUps] = useState(false);
  const [editingSignatureId, setEditingSignatureId] = useState(null);
  const [isLoadingSignatures, setIsLoadingSignatures] = useState(false);
  const [, setIsSavingSignatures] = useState(false);
  const toast = useToast();
  const {
    withCreditWarning,
    isWarningOpen,
    warningData,
    handleConfirm,
    handleOptOut,
    closeWarning,
    userSubscriptionStatus,
  } = useCreditWarning();

  /** 'panel' = user clicked Approve in this panel; 'external' = observe stream after table/header fanout */
  const approveCompleteSourceRef = useRef('panel');
  const approveStreamRef = useRef(null);
  // ── AI draft SSE streams ────────────────────────────────────────────────
  // generate/stream: captures config_id from the 'start' event; sets phase
  // to 'ready' on completion. Contact table receives and renders the rows
  // independently via its own field-definition SSE listener.
  const generateStream = useSSEStream({
    onData: (data) => {
      if (data.event === 'start' && data.config_id) {
        setGenerateConfigId(data.config_id);
        const reuseId = editingDraftIdRef.current;
        if (reuseId != null && String(data.config_id) === String(reuseId)) {
          window.dispatchEvent(new Event('messageDraftPreviewRegenerated'));
        }
      }
      if (data.event && data.event !== 'heartbeat') {
        window.dispatchEvent(new CustomEvent('mdpStreamEvent', { detail: data }));
      }
    },
    onStatus: (statusData) => setGenerateStatus(statusData),
    onComplete: () => {
      setGeneratePhase('ready');
      setGenerateStatus(null);
    },
    onError: (err) => {
      toast({
        title: 'Generation failed',
        description: err?.message || 'Could not start AI draft generation',
        status: 'error',
        duration: 4000,
      });
      setGeneratePhase('idle');
      setGenerateConfigId(null);
      setGenerateStatus(null);
    },
  });

  // approve/stream: full fan-out for all contacts. On success closes the panel.
  const approveStream = useSSEStream({
    onData: (data) => {
      if (data.event && data.event !== 'heartbeat') {
        window.dispatchEvent(new CustomEvent('mdpStreamEvent', { detail: data }));
      }
    },
    onStatus: (statusData) => setGenerateStatus(statusData),
    onComplete: () => {
      const src = approveCompleteSourceRef.current;
      approveCompleteSourceRef.current = 'panel';
      toast({ title: 'Draft saved for all contacts', status: 'success', duration: 3000 });
      setGeneratePhase('idle');
      setGenerateConfigId(null);
      setGenerateStatus(null);
      setUseAiDraft(false);
      setGeneratePrompt('');
      window.dispatchEvent(new Event('emailDraftApproved'));
      window.dispatchEvent(new Event('emailDraftCompleted'));
      if (src === 'panel') {
        onClose?.();
      }
    },
    onError: (err) => {
      approveCompleteSourceRef.current = 'panel';
      toast({
        title: 'Approve failed',
        description: err?.message || 'Could not approve draft',
        status: 'error',
        duration: 4000,
      });
      setGeneratePhase('ready');
      setGenerateStatus(null);
    },
  });
  approveStreamRef.current = approveStream;

  // Table / column header started approve fanout — mirror same config in this panel + observe stream.
  useEffect(() => {
    const onFanout = (e) => {
      const cid = e.detail?.configId;
      if (!cid || !campaignId) return;
      if (!generateConfigId || String(cid) !== String(generateConfigId)) return;
      approveCompleteSourceRef.current = 'external';
      setGeneratePhase('approving');
      setGenerateStatus({
        completion_percentage: '1',
        message: 'Applying draft to all contacts…',
      });
      approveStreamRef.current?.startStream(
        campaignService.observeDraftConfigStream(campaignId, cid),
        { method: 'GET' },
      );
    };
    window.addEventListener('emailDraftFanoutStarted', onFanout);
    return () => window.removeEventListener('emailDraftFanoutStarted', onFanout);
  }, [campaignId, generateConfigId]);

  const isGated = !typeOfMessage;
  const messageTypeOptions = typeOfMessage ? MESSAGE_TYPE_OPTIONS[typeOfMessage] : [];
  // AI injection footer + template generate: only after a **closed** //...// (both delimiters).
  // A lone opening // would otherwise flip the footer to Generate too early.
  const bodyHasAiInjection = useMemo(
    () => parseDraftSegments(body, true).some((s) => s.type === 'ai' && s.closed),
    [body]
  );
  // Backend template mode only when manual draft uses //...//. If the user turns on AI draft, we must
  // send scratch mode (body null + prompt) even if body still contains markers from before.
  const isTemplateInjectionMode = bodyHasAiInjection && !useAiDraft;
  const isEmailFollowUp = typeOfMessage === 'email' && messageType === 'follow_up';

  /** Keep Generate / progress / Approve footer visible if a preview or approve stream is in flight or awaiting Approve, even after toggling off "Generate with AI". */
  const messageDraftPipelineActive =
    generatePhase === 'generating' || generatePhase === 'approving' || generatePhase === 'ready';
  const showAiDraftFooter = useAiDraft || bodyHasAiInjection || messageDraftPipelineActive;

  // Populate form when editing; reset when creating new
  useEffect(() => {
    if (editingDraft) {
      const inferredType = editingDraft.typeOfMessage ?? LEGACY_TYPE_MAP[editingDraft.messageType] ?? 'email';
      const opts = MESSAGE_TYPE_OPTIONS[inferredType] ?? MESSAGE_TYPE_OPTIONS.email;
      const validMsgType = opts.some((o) => o.value === editingDraft.messageType)
        ? editingDraft.messageType
        : opts[0].value;

      setTypeOfMessage(inferredType);
      setEmailName(editingDraft.emailName || (inferredType === 'linkedin' ? 'New LI message' : 'New email'));
      setMessageType(validMsgType);
      setEnableHtml(editingDraft.enableHtml ?? false);

      const draftMode = editingDraft.mode;
      if (draftMode === 'scratch') {
        // AI scratch-generated draft: restore prompt so user can edit and re-run
        setUseAiDraft(true);
        setGeneratePrompt(editingDraft.aiPrompt || '');
        setSubject('');
        setBody('');
      } else if (draftMode === 'template' || draftMode === 'pure_template') {
        // Prompt-injected or pure-variable template: restore the raw //...// body
        setUseAiDraft(false);
        setGeneratePrompt('');
        setBody(editingDraft.templateBody || '');
        setSubject(
          inferredType === 'email' && validMsgType === 'follow_up'
            ? ''
            : (editingDraft.subjectTemplate || '')
        );
      } else {
        // Manual draft: restore saved templates directly
        setUseAiDraft(false);
        setGeneratePrompt('');
        setSubject(
          inferredType === 'email' && validMsgType === 'follow_up'
            ? ''
            : (editingDraft.subjectTemplate || '')
        );
        setBody(editingDraft.bodyTemplate || '');
      }
    } else {
      setTypeOfMessage('email');
      setEmailName('New email');
      setMessageType('new_email');
      setEnableHtml(false);
      setSubject('');
      setBody('');
      setUseAiDraft(false);
      setGeneratePrompt('');
    }
    appliedSigIdRef.current = null;
    setPreviewRows(null);
    setShowPreview(false);
    setShowPolishPanel(false);
    setPolishedBody(null);
    setGeneratePhase('idle');
    setGenerateConfigId(null);
  }, [editingDraft]);

  useEffect(() => {
    editingDraftIdRef.current = editingDraft?.id ?? null;
  }, [editingDraft]);

  // On mount (or when the campaign changes), check whether a draft generation or
  // approval is already in-flight so the panel can restore its state after network
  // interruptions, panel toggles, or page refreshes.
  // Only runs in create-new mode (editingDraft === null) to avoid interfering with
  // manual template editing.
  const reconnectCheckedRef = useRef(false);

  useEffect(() => {
    // Reset the guard whenever the campaign changes so a fresh check runs.
    reconnectCheckedRef.current = false;
  }, [campaignId]);

  useEffect(() => {
    if (!campaignId || editingDraft || reconnectCheckedRef.current) return;
    reconnectCheckedRef.current = true;

    const checkAndRestore = async () => {
      try {
        const data = await campaignService.getActiveDraftConfig(campaignId);
        if (!data?.config_id || data.phase === 'idle') return;

        // Restore AI-draft UI with the recovered config.
        setUseAiDraft(true);
        setGenerateConfigId(data.config_id);

        if (data.phase === 'ready') {
          // Preview is complete — user just needs to Approve or Discard.
          setGeneratePhase('ready');
        } else if (data.phase === 'generating') {
          // Preview fanout still running — reconnect to stream; onComplete → 'ready'.
          setGeneratePhase('generating');
          generateStream.startStream(
            campaignService.observeDraftConfigStream(campaignId, data.config_id),
            { method: 'GET' }
          );
        } else if (data.phase === 'approving') {
          // Approval fanout still running — reconnect to stream; onComplete → close panel.
          approveCompleteSourceRef.current = 'external';
          setGeneratePhase('approving');
          approveStream.startStream(
            campaignService.observeDraftConfigStream(campaignId, data.config_id),
            { method: 'GET' }
          );
        }
      } catch (err) {
        // Silently ignore auth errors (user not logged in / no access).
        if (err?.response?.status !== 401 && err?.response?.status !== 403) {
          console.warn('[MessageDraftingPanel] Could not restore active draft:', err);
        }
      }
    };

    checkAndRestore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, editingDraft]);

  const DEFAULT_NAMES = { email: 'New email', linkedin: 'New LI message' };
  const MESSAGE_TYPE_DEFAULT_NAMES = { new_email: 'New email', follow_up: 'Follow up' };
  const ALL_DEFAULT_NAMES = new Set([...Object.values(DEFAULT_NAMES), ...Object.values(MESSAGE_TYPE_DEFAULT_NAMES)]);

  const handleTypeOfMessageChange = (value) => {
    setTypeOfMessage(value);
    const opts = MESSAGE_TYPE_OPTIONS[value];
    if (opts?.length) setMessageType(opts[0].value);
    if (value === 'linkedin') setSubject('');
    // Auto-update the column name only when it still holds a default value
    setEmailName(prev =>
      Object.values(DEFAULT_NAMES).includes(prev) ? DEFAULT_NAMES[value] ?? prev : prev
    );
  };

  // Load signatures from backend on mount
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoadingSignatures(true);
      try {
        const data = await campaignService.getSignatures();
        if (!cancelled && data) {
          setSignatures(data.signatures || []);
          setDefaultForNewMessage(data.defaultForNewMessage || '');
          setDefaultForFollowUps(data.defaultForFollowUps || '');
        }
      } catch (err) {
        if (!cancelled && err?.response?.status !== 401) {
          toast({ title: 'Could not load signatures', status: 'warning', duration: 2000 });
        }
      } finally {
        if (!cancelled) setIsLoadingSignatures(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [toast]);

  // Auto-append default signature for new drafts. Re-runs when the default changes
  // so switching signatures in settings is reflected immediately.
  // Signatures are not appended for LinkedIn messages.
  useEffect(() => {
    if (editingDraft || isLoadingSignatures) return;

    const newSig = (typeOfMessage !== 'linkedin' && defaultForNewMessage)
      ? signatures.find(s => s.id === defaultForNewMessage)
      : null;
    const oldSig = appliedSigIdRef.current
      ? signatures.find(s => s.id === appliedSigIdRef.current)
      : null;

    if (newSig?.id === appliedSigIdRef.current) return;

    setBody(prev => {
      // Strip the previously appended signature if present
      const stripped = oldSig?.content
        ? prev.replace('\n\n' + oldSig.content, '')
        : prev;
      appliedSigIdRef.current = newSig?.id || null;
      if (!newSig?.content) return stripped;
      return (stripped || '') + '\n\n' + newSig.content;
    });
  }, [editingDraft, isLoadingSignatures, defaultForNewMessage, signatures, typeOfMessage]);

  const persistSignatures = async (nextSignatures, nextDefaultNew, nextDefaultFollow) => {
    setIsSavingSignatures(true);
    try {
      const data = await campaignService.updateSignatures({
        signatures: nextSignatures,
        defaultForNewMessage: nextDefaultNew ?? '',
        defaultForFollowUps: nextDefaultFollow ?? '',
      });
      if (data) {
        setSignatures(data.signatures || []);
        setDefaultForNewMessage(data.defaultForNewMessage || '');
        setDefaultForFollowUps(data.defaultForFollowUps || '');
      }
    } catch (err) {
      if (err?.response?.status !== 401) {
        toast({ title: 'Could not save signatures', status: 'error', duration: 3000 });
      }
      throw err;
    } finally {
      setIsSavingSignatures(false);
    }
  };

  const handleSave = async () => {
    const isScratchAiEdit = editingDraft?.mode === 'scratch' && useAiDraft;
    const requiresSubject = !isScratchAiEdit && (messageType === 'new_email' || messageType === 'linkedin_inmail');
    if (!typeOfMessage) return;
    if (isScratchAiEdit) {
      if (!generatePrompt.trim()) return;
    } else if ((!subject.trim() && !body.trim()) || (requiresSubject && !subject.trim())) {
      return;
    }
    setIsSaving(true);
    try {
      await onSave?.({
        id: editingDraft?.id,
        typeOfMessage,
        emailName,
        messageType,
        enableHtml,
        subject,
        body,
        aiPrompt: isScratchAiEdit ? generatePrompt : undefined,
        subjectColumnName: `${emailName}: subject`,
        bodyColumnName: `${emailName}: body`,
        campaignId,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenAddSignatureModal = () => {
    setEditingSignatureId(null);
    setAddSignatureName('');
    setAddSignatureContent('');
    setAddSignatureDefaultForNewMessage(false);
    setAddSignatureDefaultForFollowUps(false);
    setShowAddSignatureModal(true);
  };

  const handleEditSignature = (sig) => {
    setEditingSignatureId(sig.id);
    setAddSignatureName(sig.name);
    setAddSignatureContent(sig.content || '');
    setAddSignatureDefaultForNewMessage(defaultForNewMessage === sig.id);
    setAddSignatureDefaultForFollowUps(defaultForFollowUps === sig.id);
    setShowAddSignatureModal(true);
  };

  const handleAddSignature = async () => {
    if (!addSignatureName.trim()) {
      toast({ title: 'Enter a signature name', status: 'warning', duration: 2000 });
      return;
    }
    let nextSigs;
    let nextDefaultNew = defaultForNewMessage;
    let nextDefaultFollow = defaultForFollowUps;
    if (editingSignatureId) {
      nextSigs = signatures.map(s => s.id === editingSignatureId
        ? { ...s, name: addSignatureName.trim(), content: addSignatureContent.trim() }
        : s);
      nextDefaultNew = addSignatureDefaultForNewMessage ? editingSignatureId : (defaultForNewMessage === editingSignatureId ? '' : defaultForNewMessage);
      nextDefaultFollow = addSignatureDefaultForFollowUps ? editingSignatureId : (defaultForFollowUps === editingSignatureId ? '' : defaultForFollowUps);
      setSignatures(nextSigs);
      setDefaultForNewMessage(nextDefaultNew);
      setDefaultForFollowUps(nextDefaultFollow);
      setEditingSignatureId(null);
      toast({ title: 'Signature updated', status: 'success', duration: 2000 });
    } else {
      const id = `sig_${Date.now()}`;
      nextSigs = [...signatures, { id, name: addSignatureName.trim(), content: addSignatureContent.trim() }];
      if (addSignatureDefaultForNewMessage) nextDefaultNew = id;
      if (addSignatureDefaultForFollowUps) nextDefaultFollow = id;
      if (signatures.length === 0 && !addSignatureDefaultForNewMessage && !addSignatureDefaultForFollowUps) {
        nextDefaultNew = id;
        nextDefaultFollow = id;
      }
      setSignatures(nextSigs);
      setDefaultForNewMessage(nextDefaultNew);
      setDefaultForFollowUps(nextDefaultFollow);
      toast({ title: 'Signature added', status: 'success', duration: 2000 });
    }
    setAddSignatureName('');
    setAddSignatureContent('');
    setAddSignatureDefaultForNewMessage(false);
    setAddSignatureDefaultForFollowUps(false);
    setShowAddSignatureModal(false);
    try {
      await persistSignatures(nextSigs, nextDefaultNew, nextDefaultFollow);
    } catch {
      // Toast already shown in persistSignatures
    }
  };

  const handleDeleteSignature = async (id) => {
    try {
      const data = await campaignService.deleteSignature(id);
      if (data) {
        setSignatures(data.signatures || []);
        setDefaultForNewMessage(data.defaultForNewMessage || '');
        setDefaultForFollowUps(data.defaultForFollowUps || '');
      }
      toast({ title: 'Signature deleted', status: 'success', duration: 2000 });
    } catch (err) {
      if (err?.response?.status !== 401) {
        toast({ title: 'Could not delete signature', status: 'error', duration: 3000 });
      }
    }
  };

  const handleDefaultForNewMessageChange = async (id) => {
    setDefaultForNewMessage(id);
    try {
      await persistSignatures(signatures, id, defaultForFollowUps);
    } catch {
      setDefaultForNewMessage(defaultForNewMessage); // revert on error
    }
  };

  const handleDefaultForFollowUpsChange = async (id) => {
    setDefaultForFollowUps(id);
    try {
      await persistSignatures(signatures, defaultForNewMessage, id);
    } catch {
      setDefaultForFollowUps(defaultForFollowUps); // revert on error
    }
  };

  const handlePreview = async () => {
    if (!subject.trim() && !body.trim()) {
      toast({ title: 'Enter a subject or body to preview', status: 'warning', duration: 2000 });
      return;
    }
    if (!campaignId) {
      toast({ title: 'No campaign selected', status: 'warning', duration: 2000 });
      return;
    }
    setShowPolishPanel(false);
    setIsPreviewing(true);
    try {
      const result = await campaignService.previewMessageDraft(campaignId, subject, body);
      setPreviewRows(result.rows || []);
      setShowPreview(true);
    } catch (error) {
      toast({ title: 'Preview failed', description: error?.response?.data?.detail || 'Could not generate preview', status: 'error', duration: 3000 });
    } finally {
      setIsPreviewing(false);
    }
  };

  const handlePolish = async () => {
    if (!body.trim()) return;
    setShowPreview(false);
    setShowPolishPanel(true);
    setPolishedBody(null);
    setIsPolishing(true);
    try {
      const result = await campaignService.polishBody(body, typeOfMessage);
      setPolishedBody(result.polished_body ?? '');
    } catch (error) {
      toast({
        title: 'Polish failed',
        description: error?.response?.data?.detail || 'Could not polish message',
        status: 'error',
        duration: 3000,
      });
      setShowPolishPanel(false);
    } finally {
      setIsPolishing(false);
    }
  };

  // Abort any in-flight AI draft streams when the component unmounts.
  useEffect(() => () => {
    generateStream.cleanup();
    approveStream.cleanup();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── AI draft handlers ────────────────────────────────────────────────────

  const handleGenerate = async () => {
    // Scratch mode requires a prompt; template injection mode just needs a body with //...//
    if (!isTemplateInjectionMode && !generatePrompt.trim()) return;
    if (!campaignId) return;
    setGeneratePhase('generating');
    setGenerateConfigId(null);
    await generateStream.startStream(
      campaignService.generateMessageDraft(campaignId),
      {
        method: 'POST',
        body: {
          email_name: emailName,
          type_of_message: typeOfMessage,
          message_type: messageType,
          // Template mode: pass body (with //...// markers) and subject so the
          // backend creates both Email Subject and Email Body columns.
          // Scratch mode: body=null so the LLM generates everything from prompt.
          subject: isTemplateInjectionMode ? (subject || undefined) : undefined,
          body: isTemplateInjectionMode ? body : null,
          prompt: isTemplateInjectionMode ? '' : generatePrompt,
          enable_html: enableHtml,
          ...(editingDraft?.id ? { reuse_existing_config_id: editingDraft.id } : {}),
        },
      }
    );
  };

  const handleApprove = async () => {
    if (!generateConfigId || generatePhase !== 'ready') return;
    approveCompleteSourceRef.current = 'panel';
    setGeneratePhase('approving');

    const doApprove = () => approveStream.startStream(
      campaignService.approveDraftConfig(campaignId, generateConfigId),
      { method: 'POST' }
    );

    try {
      await withCreditWarning(
        'email_draft',
        { campaign_id: campaignId, config_id: generateConfigId, entity_type: 'contact' },
        doApprove
      );
    } catch (err) {
      if (!err?.isCancellation) {
        toast({
          title: 'Approval failed',
          description: err?.message || 'Could not approve draft',
          status: 'error',
          duration: 4000,
        });
      }
      setGeneratePhase('ready');
    }
  };

  const handleDiscard = async () => {
    const configToDelete = generateConfigId;
    const skipRejectDeletingExistingBundle =
      Boolean(
        configToDelete &&
          editingDraft?.id &&
          String(editingDraft.id) === String(configToDelete),
      );

    setGeneratePhase('idle');
    setGenerateConfigId(null);
    setGenerateStatus(null);

    if (!configToDelete) return;

    if (!skipRejectDeletingExistingBundle) {
      try {
        await campaignService.rejectDraftPreview(campaignId, configToDelete);
      } catch (err) {
        console.error('[AI draft] Discard failed — config may persist in DB:', err);
      }
      window.dispatchEvent(new Event('mdpDraftDiscarded'));
    } else {
      try {
        await campaignService.restoreMessageDraftPreview(campaignId, configToDelete);
        toast({
          title: 'Preview discarded',
          description: 'This draft was restored to its previous state.',
          status: 'success',
          duration: 3000,
        });
        // Dedicated event: full table refresh + clear preview chrome (do not use
        // messageDraftPreviewRegenerated — that also fires at SSE start for recycle).
        window.dispatchEvent(new Event('messageDraftPreviewRestored'));
      } catch (err) {
        console.error('[AI draft] Discard restore failed:', err);
        const detail = err?.response?.data?.detail;
        toast({
          title: 'Could not restore previous draft',
          description:
            typeof detail === 'string'
              ? detail
              : 'Try refreshing the page.',
          status: 'warning',
          duration: 4000,
        });
      }
    }
  };

  // Track the panel's position for the portal-based preview
  const panelRef = useRef(null);
  const [panelRect, setPanelRect] = useState(null);

  useEffect(() => {
    if ((showPreview || showPolishPanel) && panelRef.current) {
      const rect = panelRef.current.getBoundingClientRect();
      setPanelRect(rect);
    }
  }, [showPreview, showPolishPanel]);

  // Also update on resize
  useEffect(() => {
    if (!showPreview && !showPolishPanel) return;
    const handleResize = () => {
      if (panelRef.current) {
        setPanelRect(panelRef.current.getBoundingClientRect());
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showPreview, showPolishPanel]);

  return (
    <Box h="100%" display="flex" flexDirection="row" bg="#0a0a0a" position="relative" ref={panelRef}>
      {/* Preview Panel — rendered via Portal to escape overflow:hidden parent */}
      {showPreview && panelRect && (
        <Portal>
          <Box
            position="fixed"
            top={`${panelRect.top + 8}px`}
            left={`${Math.max(8, panelRect.left - 420 - 10)}px`}
            w="420px"
            maxH={`${Math.min(panelRect.height - 16, 480)}px`}
            bg="#0a0a0a"
            border="1px solid rgba(255,255,255,0.08)"
            display="flex"
            flexDirection="column"
            zIndex={998}
            boxShadow="0 8px 32px rgba(0,0,0,0.6), -4px 0 16px rgba(0,0,0,0.3)"
            borderRadius="12px"
          >
            <HStack px={4} py={3} borderBottom="1px solid rgba(255,255,255,0.08)" justify="space-between" borderTopRadius="12px">
              <Text color="#fafafa" fontSize="md" fontWeight="semibold">Preview</Text>
              <Button
                variant="ghost"
                size="sm"
                color="#a3a3a3"
                rightIcon={<FiChevronRight />}
                onClick={() => setShowPreview(false)}
                _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
                _focus={{ boxShadow: 'none' }}
              >
                Hide preview
              </Button>
            </HStack>
            <Box flex={1} overflowY="auto">
              {isPreviewing ? (
                <VStack py={10} spacing={3}>
                  <Spinner size="md" color="rgba(255,255,255,0.4)" />
                  <Text color="rgba(255,255,255,0.4)" fontSize="sm">Generating preview...</Text>
                </VStack>
              ) : previewRows && previewRows.length > 0 ? (
                <Box>
                  {/* Table header */}
                  <HStack
                    px={4} py={2}
                    borderBottom="1px solid rgba(255,255,255,0.08)"
                    bg="rgba(255,255,255,0.02)"
                    spacing={4}
                  >
                    <Text color="rgba(255,255,255,0.3)" fontSize="xs" fontWeight="bold" w="36px" flexShrink={0}>Row</Text>
                    <Text color="rgba(255,255,255,0.3)" fontSize="xs" fontWeight="bold" flex={1}>Output</Text>
                  </HStack>
                  {/* Table rows */}
                  {previewRows.map((row) => (
                    <Box key={row.row} borderBottom="1px solid rgba(255,255,255,0.06)">
                      <HStack px={4} py={3} spacing={4} align="flex-start">
                        <Text color="rgba(255,255,255,0.3)" fontSize="sm" w="36px" flexShrink={0} pt="2px">{row.row}</Text>
                        <VStack spacing={1} align="stretch" flex={1} minW={0}>
                          <Text color="#fafafa" fontSize="sm" fontWeight="medium" wordBreak="break-word">
                            {row.subject || <Text as="span" color="rgba(255,255,255,0.2)" fontStyle="italic">empty subject</Text>}
                          </Text>
                          <Text color="rgba(255,255,255,0.55)" fontSize="sm" wordBreak="break-word" whiteSpace="pre-wrap">
                            {row.body || <Text as="span" color="rgba(255,255,255,0.2)" fontStyle="italic">empty body</Text>}
                          </Text>
                        </VStack>
                      </HStack>
                    </Box>
                  ))}
                </Box>
              ) : previewRows && previewRows.length === 0 ? (
                <VStack py={10} spacing={2}>
                  <Text color="rgba(255,255,255,0.3)" fontSize="sm">No contacts found in this campaign.</Text>
                </VStack>
              ) : null}
            </Box>
          </Box>
        </Portal>
      )}

      {/* Polish Panel — Gmail-style side panel for polished body text */}
      {showPolishPanel && panelRect && (
        <Portal>
          <Box
            position="fixed"
            top={`${panelRect.top + 8}px`}
            left={`${Math.max(8, panelRect.left - 420 - 10)}px`}
            w="420px"
            maxH={`${Math.min(panelRect.height - 16, 480)}px`}
            bg="#0a0a0a"
            border="1px solid rgba(255,255,255,0.08)"
            display="flex"
            flexDirection="column"
            zIndex={999}
            boxShadow="0 8px 32px rgba(0,0,0,0.6), -4px 0 16px rgba(0,0,0,0.3)"
            borderRadius="12px"
          >
            <HStack px={4} py={3} borderBottom="1px solid rgba(255,255,255,0.08)" justify="space-between" borderTopRadius="12px">
              <HStack spacing={2}>
                <Text color="#fafafa" fontSize="sm" fontWeight="semibold" letterSpacing="0.01em">Polished version</Text>
                <IconButton
                  icon={<LuRefreshCw size={13} />}
                  aria-label="Recreate"
                  size="xs"
                  variant="ghost"
                  color="rgba(255,255,255,0.35)"
                  borderRadius="5px"
                  isLoading={isPolishing}
                  isDisabled={isPolishing}
                  onClick={handlePolish}
                  _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
                  _focus={{ boxShadow: 'none' }}
                />
              </HStack>
              <HStack spacing={1}>
                <Button
                  size="sm"
                  variant="ghost"
                  color="#a3a3a3"
                  fontWeight="normal"
                  fontSize="sm"
                  px={3}
                  h="28px"
                  borderRadius="6px"
                  onClick={() => {
                    setShowPolishPanel(false);
                    setPolishedBody(null);
                  }}
                  _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
                  _active={{ bg: 'rgba(255,255,255,0.08)' }}
                  _focus={{ boxShadow: 'none' }}
                >
                  Dismiss
                </Button>
                <Button
                  size="sm"
                  fontWeight="600"
                  fontSize="sm"
                  px={3}
                  h="28px"
                  borderRadius="full"
                  isDisabled={!polishedBody}
                  bg="#fafafa"
                  color="#0a0a0a"
                  _hover={{ opacity: 0.85 }}
                  _active={{ opacity: 0.75 }}
                  _disabled={{ opacity: 0.35, cursor: 'not-allowed' }}
                  _focus={{ boxShadow: 'none' }}
                  onClick={() => {
                    setBody(polishedBody || '');
                    setShowPolishPanel(false);
                    setPolishedBody(null);
                  }}
                >
                  Replace
                </Button>
              </HStack>
            </HStack>
            <Box flex={1} overflowY="auto" p={4}>
              {isPolishing ? (
                <VStack align="stretch" spacing="10px" pt={1}>
                  <Box className="polish-shimmer-line" w="92%" />
                  <Box className="polish-shimmer-line" w="100%" />
                  <Box className="polish-shimmer-line" w="85%" />
                  <Box className="polish-shimmer-line" w="60%" />
                  <Box h="6px" />
                  <Box className="polish-shimmer-line" w="97%" />
                  <Box className="polish-shimmer-line" w="78%" />
                  <Box className="polish-shimmer-line" w="88%" />
                  <Box h="6px" />
                  <Box className="polish-shimmer-line" w="55%" />
                </VStack>
              ) : polishedBody ? (
                <Text color="rgba(255,255,255,0.75)" fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-word">
                  {polishedBody}
                </Text>
              ) : (
                <Text color="rgba(255,255,255,0.3)" fontSize="sm" fontStyle="italic">
                  Polish will appear here.
                </Text>
              )}
            </Box>
          </Box>
        </Portal>
      )}

      {/* Main drafting form */}
      <Box h="100%" display="flex" flexDirection="column" flex={1} minW={0}>
        <HStack px={4} py={3} borderBottom="1px solid rgba(255,255,255,0.08)" justify="space-between">
          {showSignaturesView ? (
            <>
              <HStack spacing={2}>
                <IconButton
                  icon={<LuChevronLeft size={18} />}
                  variant="ghost"
                  color="#a3a3a3"
                  size="sm"
                  aria-label="Back"
                  onClick={() => setShowSignaturesView(false)}
                  _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
                  _focus={{ boxShadow: 'none', outline: 'none' }}
                  _focusVisible={{ boxShadow: 'none', outline: 'none' }}
                />
                <Text color="#fafafa" fontSize="md" fontWeight="semibold">Signatures</Text>
              </HStack>
              <IconButton
                icon={<FiX />}
                onClick={onClose}
                variant="ghost"
                color="#a3a3a3"
                fontSize="20px"
                size="sm"
                aria-label="Close"
                transition="all 0.25s ease-in-out"
                _hover={{ transform: "rotate(90deg)", fontSize: "25px", color: "red.400", bg: "transparent" }}
                _focus={{ boxShadow: 'none', outline: 'none' }}
                _focusVisible={{ boxShadow: 'none', outline: 'none' }}
              />
            </>
          ) : (
            <>
              <Text color="#fafafa" fontSize="md" fontWeight="semibold">Message drafting</Text>
              <HStack spacing={0}>
                <Menu placement="bottom-end">
                  <MenuButton
                    as={IconButton}
                    icon={<LuSettings size={18} />}
                    variant="ghost"
                    color="#a3a3a3"
                    size="sm"
                    aria-label="Settings"
                    _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
                    _focus={{ boxShadow: 'none', outline: 'none' }}
                    _focusVisible={{ boxShadow: 'none', outline: 'none' }}
                  />
                  <MenuList
                    bg="#0f0f0f"
                    border="1px solid"
                    borderColor="rgba(255,255,255,0.10)"
                    borderRadius="lg"
                    py={1}
                    px={1}
                    minW="160px"
                    shadow="xl"
                    zIndex={1500}
                  >
                    <MenuItem
                      icon={<LuPencil size={14} />}
                      bg="#0f0f0f"
                      color="#fafafa"
                      fontSize="sm"
                      _hover={{ bg: 'rgba(255,255,255,0.06)' }}
                      _focus={{ bg: 'rgba(255,255,255,0.06)' }}
                      borderRadius="md"
                      mx={0.5}
                      onClick={() => setShowSignaturesView(true)}
                    >
                      Signature
                    </MenuItem>
                  </MenuList>
                </Menu>
                <IconButton
                  icon={<FiX />}
                  onClick={onClose}
                  variant="ghost"
                  color="#a3a3a3"
                  fontSize="20px"
                  size="sm"
                  aria-label="Close"
                  transition="all 0.25s ease-in-out"
                  _hover={{ transform: "rotate(90deg)", fontSize: "25px", color: "red.400", bg: "transparent" }}
                  _focus={{ boxShadow: 'none', outline: 'none' }}
                />
              </HStack>
            </>
          )}
        </HStack>
        <Box flex={1} overflowY="auto" px={4} py={4}>
          {showSignaturesView ? (
            <VStack spacing={5} align="stretch">
              {isLoadingSignatures ? (
                <HStack justify="center" py={8}>
                  <Spinner size="sm" color="rgba(255,255,255,0.4)" />
                  <Text color="#a3a3a3" fontSize="sm">Loading signatures...</Text>
                </HStack>
              ) : (
                <>
              <Text color="rgba(255,255,255,0.3)" fontSize="sm">Add reusable text blocks to append to your messages. Choose default signatures for new messages and follow-ups.</Text>
              <HStack justify="space-between">
                <Button
                  leftIcon={<LuPlus size={16} />}
                  size="sm"
                  variant="outline"
                  borderRadius="full"
                  borderColor="rgba(255,255,255,0.12)"
                  color="#a3a3a3"
                  fontSize="sm"
                  _hover={{ bg: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.25)', color: '#fafafa' }}
                  _focus={{ boxShadow: 'none', outline: 'none' }}
                  _focusVisible={{ boxShadow: 'none', outline: 'none' }}
                  onClick={handleOpenAddSignatureModal}
                >
                  Add signature
                </Button>
              </HStack>
              <FormControl>
                <FormLabel color="#a3a3a3" fontSize="sm" fontWeight="medium" mb={2}>Default for new messages</FormLabel>
                <Menu placement="bottom-start">
                  <MenuButton
                    as={Button}
                    rightIcon={<LuChevronDown size={14} />}
                    variant="outline"
                    w="100%"
                    justifyContent="space-between"
                    textAlign="left"
                    fontWeight="normal"
                    size="sm"
                    h="32px"
                    bg="#111111"
                    border="1px solid"
                    borderColor="rgba(255,255,255,0.10)"
                    color="#fafafa"
                    fontSize="sm"
                    borderRadius="lg"
                    _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
                    _expanded={{ borderColor: 'rgba(255,255,255,0.3)' }}
                    _focus={{ boxShadow: 'none', outline: 'none' }}
                    _focusVisible={{ boxShadow: 'none', outline: 'none' }}
                  >
                    {signatures.find(s => s.id === defaultForNewMessage)?.name ?? '(no signature)'}
                  </MenuButton>
                  <MenuList
                    bg="#0f0f0f"
                    border="1px solid"
                    borderColor="rgba(255,255,255,0.10)"
                    borderRadius="lg"
                    py={1}
                    px={1}
                    minW="180px"
                    shadow="xl"
                    zIndex={1500}
                  >
                    <MenuItem
                      onClick={() => handleDefaultForNewMessageChange('')}
                      bg="#0f0f0f"
                      color="#fafafa"
                      fontSize="sm"
                      _hover={{ bg: 'rgba(255,255,255,0.06)' }}
                      _focus={{ bg: 'rgba(255,255,255,0.06)' }}
                      borderRadius="md"
                      mx={0.5}
                    >
                      (no signature)
                    </MenuItem>
                    {signatures.map(s => (
                      <MenuItem
                        key={s.id}
                        onClick={() => handleDefaultForNewMessageChange(s.id)}
                        bg="#0f0f0f"
                        color="#fafafa"
                        fontSize="sm"
                        _hover={{ bg: 'rgba(255,255,255,0.06)' }}
                        _focus={{ bg: 'rgba(255,255,255,0.06)' }}
                        borderRadius="md"
                        mx={0.5}
                      >
                        {s.name}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </FormControl>
              <FormControl>
                <FormLabel color="#a3a3a3" fontSize="sm" fontWeight="medium" mb={2}>Default for follow-ups</FormLabel>
                <Menu placement="bottom-start">
                  <MenuButton
                    as={Button}
                    rightIcon={<LuChevronDown size={14} />}
                    variant="outline"
                    w="100%"
                    justifyContent="space-between"
                    textAlign="left"
                    fontWeight="normal"
                    size="sm"
                    h="32px"
                    bg="#111111"
                    border="1px solid"
                    borderColor="rgba(255,255,255,0.10)"
                    color="#fafafa"
                    fontSize="sm"
                    borderRadius="lg"
                    _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
                    _expanded={{ borderColor: 'rgba(255,255,255,0.3)' }}
                    _focus={{ boxShadow: 'none', outline: 'none' }}
                    _focusVisible={{ boxShadow: 'none', outline: 'none' }}
                  >
                    {signatures.find(s => s.id === defaultForFollowUps)?.name ?? '(no signature)'}
                  </MenuButton>
                  <MenuList
                    bg="#0f0f0f"
                    border="1px solid"
                    borderColor="rgba(255,255,255,0.10)"
                    borderRadius="lg"
                    py={1}
                    px={1}
                    minW="180px"
                    shadow="xl"
                    zIndex={1500}
                  >
                    <MenuItem
                      onClick={() => handleDefaultForFollowUpsChange('')}
                      bg="#0f0f0f"
                      color="#fafafa"
                      fontSize="sm"
                      _hover={{ bg: 'rgba(255,255,255,0.06)' }}
                      _focus={{ bg: 'rgba(255,255,255,0.06)' }}
                      borderRadius="md"
                      mx={0.5}
                    >
                      (no signature)
                    </MenuItem>
                    {signatures.map(s => (
                      <MenuItem
                        key={s.id}
                        onClick={() => handleDefaultForFollowUpsChange(s.id)}
                        bg="#0f0f0f"
                        color="#fafafa"
                        fontSize="sm"
                        _hover={{ bg: 'rgba(255,255,255,0.06)' }}
                        _focus={{ bg: 'rgba(255,255,255,0.06)' }}
                        borderRadius="md"
                        mx={0.5}
                      >
                        {s.name}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              </FormControl>
              <Box>
                <Text color="#a3a3a3" fontSize="sm" fontWeight="medium" mb={2}>Stored signatures</Text>
                {signatures.length === 0 ? (
                  <Text color="rgba(255,255,255,0.3)" fontSize="sm">No signatures yet. Add one above.</Text>
                ) : (
                  <VStack spacing={2} align="stretch">
                    {signatures.map((sig) => (
                      <HStack
                        key={sig.id}
                        p={3}
                        bg="rgba(255,255,255,0.03)"
                        borderRadius="10px"
                        border="1px solid"
                        borderColor="rgba(255,255,255,0.08)"
                        justify="space-between"
                      >
                        <Text color="#fafafa" fontSize="sm">{sig.name}</Text>
                        <HStack spacing={1}>
                          <IconButton
                            icon={<LuPencil size={14} />}
                            variant="ghost"
                            size="xs"
                            color="#a3a3a3"
                            aria-label="Edit"
                            _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
                            _focus={{ boxShadow: 'none', outline: 'none' }}
                            _focusVisible={{ boxShadow: 'none', outline: 'none' }}
                            onClick={() => handleEditSignature(sig)}
                          />
                          <IconButton
                            icon={<LuTrash2 size={14} />}
                            variant="ghost"
                            size="xs"
                            color="#a3a3a3"
                            aria-label="Delete"
                            _hover={{ color: 'rgba(239,68,68,0.85)', bg: 'rgba(239,68,68,0.08)' }}
                            _focus={{ boxShadow: 'none', outline: 'none' }}
                            _focusVisible={{ boxShadow: 'none', outline: 'none' }}
                            onClick={() => handleDeleteSignature(sig.id)}
                          />
                        </HStack>
                      </HStack>
                    ))}
                  </VStack>
                )}
              </Box>
                </>
              )}
            </VStack>
          ) : (
          <VStack spacing={5} align="stretch">
            {/* Mode selector — sits at the top, above everything else */}
            <HStack justify="space-between" align="center">
              <Box>
                <Text color="rgba(255,255,255,0.75)" fontSize="sm" fontWeight="medium">Draft manually/ Generate with AI</Text>
                <Text color="rgba(255,255,255,0.3)" fontSize="xs">
                  {useAiDraft
                    ? 'Describe what to write — AI drafts a personalised message for each contact.'
                    : 'Off — write subject and body yourself using variable placeholders.'}
                </Text>
              </Box>
              <Switch
                isChecked={useAiDraft}
                onChange={(e) => {
                  const on = e.target.checked;
                  setUseAiDraft(on);
                  if (!on) {
                    setShowPolishPanel(false);
                    setPolishedBody(null);
                    // Do not abort preview/approve SSE or reset phase while a stream is active or
                    // preview is ready — that kills mdpStreamEvent → table stops updating and the
                    // status bar disappears.
                    const keepPipeline =
                      generatePhase === 'generating' ||
                      generatePhase === 'approving' ||
                      generatePhase === 'ready';
                    if (keepPipeline) return;
                    setGeneratePrompt('');
                    generateStream.stopStream();
                    approveStream.stopStream();
                    setGeneratePhase('idle');
                    setGenerateConfigId(null);
                    setGenerateStatus(null);
                  }
                }}
                colorScheme="whiteAlpha"
                size="md"
              />
            </HStack>

            {/* Type of message — required first, gates all below */}
            <Box>
              <Text color="#a3a3a3" fontSize="sm" fontWeight="medium" mb={2}>Type of message</Text>
              <HStack spacing={0} bg="rgba(255,255,255,0.04)" borderRadius="lg" p={0.5} border="1px solid" borderColor="rgba(255,255,255,0.10)">
                <Button
                  size="sm"
                  variant="ghost"
                  flex={1}
                  leftIcon={<LuMail size={16} />}
                  color={typeOfMessage === 'email' ? '#fafafa' : 'rgba(255,255,255,0.4)'}
                  bg={typeOfMessage === 'email' ? 'rgba(255,255,255,0.10)' : 'transparent'}
                  _hover={{ bg: typeOfMessage === 'email' ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.06)' }}
                  _focus={{ boxShadow: 'none', outline: 'none' }}
                  _focusVisible={{ boxShadow: 'none', outline: 'none' }}
                  borderRadius="md"
                  onClick={() => handleTypeOfMessageChange('email')}
                >
                  Email message
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  flex={1}
                  leftIcon={<LuLinkedin size={16} />}
                  color={typeOfMessage === 'linkedin' ? '#fafafa' : 'rgba(255,255,255,0.4)'}
                  bg={typeOfMessage === 'linkedin' ? 'rgba(255,255,255,0.10)' : 'transparent'}
                  _hover={{ bg: typeOfMessage === 'linkedin' ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.06)' }}
                  _focus={{ boxShadow: 'none', outline: 'none' }}
                  _focusVisible={{ boxShadow: 'none', outline: 'none' }}
                  borderRadius="md"
                  onClick={() => handleTypeOfMessageChange('linkedin')}
                >
                  LinkedIn message
                </Button>
              </HStack>
            </Box>
            {/* Gated section — disabled until type selected */}
            <Box
              opacity={isGated ? 0.5 : 1}
              pointerEvents={isGated ? 'none' : 'auto'}
              transition="opacity 0.2s ease"
            >
              <VStack spacing={5} align="stretch">
                {SHOW_ENABLE_HTML_PANEL && typeOfMessage === 'email' && (
                  <HStack justify="space-between">
                    <Box>
                      <Text color="rgba(255,255,255,0.75)" fontSize="sm" fontWeight="medium">Enable HTML</Text>
                      <Text color="rgba(255,255,255,0.3)" fontSize="xs">Enable HTML mode to use rich text formatting in your emails.</Text>
                    </Box>
                    <Switch isChecked={enableHtml} onChange={(e) => setEnableHtml(e.target.checked)} colorScheme="whiteAlpha" size="md" />
                  </HStack>
                )}
                <HStack spacing={4} align="start">
                  <Box flex={1}>
                    <Text color="#a3a3a3" fontSize="sm" fontWeight="medium" mb={2}>Column name (internal only)</Text>
                    <Input
                      value={emailName}
                      onChange={(e) => setEmailName(e.target.value)}
                      size="sm"
                      h="32px"
                      bg="#111111"
                      border="1px solid"
                      borderColor="rgba(255,255,255,0.12)"
                      color="#fafafa"
                      fontSize="sm"
                      borderRadius="lg"
                      _placeholder={{ color: 'rgba(255,255,255,0.25)' }}
                      _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
                      _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
                    />
                  </Box>
                  <Box flex={1}>
                    <Text color="#a3a3a3" fontSize="sm" fontWeight="medium" mb={2}>Message type</Text>
                    <Menu placement="bottom-start">
                      <MenuButton
                        as={Button}
                        rightIcon={<LuChevronDown size={14} />}
                        variant="outline"
                        w="100%"
                        justifyContent="space-between"
                        textAlign="left"
                        fontWeight="normal"
                        size="sm"
                        h="32px"
                        bg="#111111"
                        border="1px solid"
                        borderColor="rgba(255,255,255,0.12)"
                        color="#fafafa"
                        fontSize="sm"
                        borderRadius="lg"
                        _hover={{ borderColor: 'rgba(255,255,255,0.2)' }}
                        _expanded={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
                        _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' }}
                      >
                        {messageTypeOptions.find((o) => o.value === messageType)?.label ?? 'Select'}
                      </MenuButton>
                      <MenuList
                        bg="#0f0f0f"
                        border="1px solid"
                        borderColor="rgba(255,255,255,0.10)"
                        borderRadius="lg"
                        py={1}
                        px={1}
                        minW="160px"
                        shadow="xl"
                        zIndex={1500}
                      >
                        {messageTypeOptions.map((opt) => (
                          <MenuItem
                            key={opt.value}
                            onClick={() => {
                              setMessageType(opt.value);
                              if (typeOfMessage === 'linkedin' && opt.value !== 'linkedin_inmail') setSubject('');
                              if (typeOfMessage === 'email' && opt.value === 'follow_up') setSubject('');
                              if (typeOfMessage === 'email' && MESSAGE_TYPE_DEFAULT_NAMES[opt.value]) {
                                setEmailName(prev =>
                                  ALL_DEFAULT_NAMES.has(prev) ? MESSAGE_TYPE_DEFAULT_NAMES[opt.value] : prev
                                );
                              }
                            }}
                            bg="#0f0f0f"
                            color="#fafafa"
                            fontSize="sm"
                            _hover={{ bg: 'rgba(255,255,255,0.06)' }}
                            _focus={{ bg: 'rgba(255,255,255,0.06)' }}
                            borderRadius="md"
                            mx={0.2}
                          >
                            {opt.label}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                  </Box>
                </HStack>

                {/* AI mode: prompt textarea replaces Subject + Body */}
                {useAiDraft ? (
                  <Box>
                    {isEmailFollowUp && (
                      <Box mb={3}>
                        <Text color="#a3a3a3" fontSize="sm" fontWeight="medium" mb={2}>Subject</Text>
                        <Box
                          borderRadius="lg"
                          border="1px solid"
                          borderColor="rgba(255,255,255,0.1)"
                          bg="rgba(255,255,255,0.03)"
                          px={3}
                          py={2.5}
                        >
                          <Text color="rgba(255,255,255,0.78)" fontSize="sm" lineHeight="1.55">
                            {EMAIL_FOLLOW_UP_THREAD_COPY}
                          </Text>
                        </Box>
                      </Box>
                    )}
                    <Text color="#a3a3a3" fontSize="sm" fontWeight="medium" mb={2}>Prompt</Text>
                    <VariableChipInput
                      value={generatePrompt}
                      onChange={setGeneratePrompt}
                      placeholder="Describe the message you want to send… e.g. 'Write a warm intro email to a VP of Engineering about our API product'"
                      isTextarea
                      minH="120px"
                      hideHint
                    />
                  </Box>
                ) : (
                  <>
                    {isEmailFollowUp ? (
                      <Box mb={1}>
                        <Text color="#a3a3a3" fontSize="sm" fontWeight="medium" mb={2}>Subject</Text>
                        <Box
                          borderRadius="lg"
                          border="1px solid"
                          borderColor="rgba(255,255,255,0.1)"
                          bg="rgba(255,255,255,0.03)"
                          px={3}
                          py={2.5}
                        >
                          <Text color="rgba(255,255,255,0.78)" fontSize="sm" lineHeight="1.55">
                            {EMAIL_FOLLOW_UP_THREAD_COPY}
                          </Text>
                        </Box>
                      </Box>
                    ) : (
                      (typeOfMessage === 'email' || (typeOfMessage === 'linkedin' && messageType === 'linkedin_inmail')) && (
                        <Box>
                          <Text color="#a3a3a3" fontSize="sm" fontWeight="medium" mb={2}>Subject</Text>
                          <VariableChipInput value={subject} onChange={setSubject} placeholder="Subject text, type {{...}} for custom fields" minH="48px" hideHint />
                        </Box>
                      )
                    )}

                    <Box>
                      <HStack justify="space-between" align="center" mb={2}>
                        <Text color="#a3a3a3" fontSize="sm" fontWeight="medium">Body</Text>
                        <IconButton
                          icon={<PolishIcon size={18} />}
                          aria-label="Polish message"
                          size="sm"
                          variant="ghost"
                          color="rgba(255,255,255,0.35)"
                          borderRadius="full"
                          isLoading={isPolishing}
                          isDisabled={!body.trim() || isGated}
                          onClick={handlePolish}
                          _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.08)' }}
                          _active={{ bg: 'rgba(255,255,255,0.12)' }}
                          _focus={{ boxShadow: 'none' }}
                        />
                      </HStack>
                      <VariableChipInput
                        value={body}
                        onChange={setBody}
                        placeholder="Body text, type {{...}} for custom fields and //...// for AI prompt injection"
                        isTextarea
                        minH="96px"
                        enableAiPromptChips
                      />
                      {typeOfMessage === 'linkedin' && messageType === 'connection_message' && (
                        <Text color="rgba(255,255,255,0.3)" fontSize="xs" mt={1} textAlign="right">
                          {body.length}/300
                        </Text>
                      )}
                    </Box>
                  </>
                )}
              </VStack>
            </Box>
          </VStack>
          )}
        </Box>
        {generateStatus && (generatePhase === 'generating' || generatePhase === 'approving') && (
          <Box px={4} pt={2}>
            <CampaignStatusBar
              statusUpdate={generateStatus}
              operationType="email_drafts"
            />
          </Box>
        )}
        {!showSignaturesView && (
        <HStack px={4} py={3} borderTop="1px solid rgba(255,255,255,0.08)" justify="space-between">
          {showAiDraftFooter ? (
            /* ── AI draft footer: state machine ─────────────────────────────
               idle       → [Cancel] [Generate]
               generating → [Cancel] [Generating… ⟳]  (stream in flight)
               ready      → [Discard]  [Approve]
               approving  → [Discard·] [Approving… ⟳]
            ──────────────────────────────────────────────────────────────── */
            <>
              {generatePhase === 'idle' && (
                <>
                  <Box />
                  <HStack spacing={2}>
                    <Button variant="ghost" size="sm" color="#a3a3a3" borderRadius="full" _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }} _focus={{ boxShadow: 'none' }} onClick={onClose}>Cancel</Button>
                    <Button
                      size="sm"
                      bg="#fafafa"
                      color="#0a0a0a"
                      borderRadius="full"
                      fontWeight="600"
                      isDisabled={isGated || (!isTemplateInjectionMode && !generatePrompt.trim())}
                      onClick={() => handleGenerate()}
                      _hover={{ opacity: 0.85 }}
                      _active={{ opacity: 0.75 }}
                      _disabled={{ opacity: 0.35, cursor: 'not-allowed' }}
                      _focus={{ boxShadow: 'none' }}
                    >
                      Generate
                    </Button>
                  </HStack>
                </>
              )}

              {generatePhase === 'generating' && (
                <>
                  <Box />
                  <HStack spacing={2}>
                    <Button variant="ghost" size="sm" color="rgba(255,255,255,0.2)" borderRadius="full" _focus={{ boxShadow: 'none' }} isDisabled>Cancel</Button>
                    <Button
                      size="sm"
                      bg="#fafafa"
                      color="#0a0a0a"
                      borderRadius="full"
                      fontWeight="600"
                      isLoading
                      loadingText="Generating…"
                      _disabled={{ opacity: 0.55 }}
                      _focus={{ boxShadow: 'none' }}
                    >
                      Generating
                    </Button>
                  </HStack>
                </>
              )}

              {generatePhase === 'ready' && (
                <>
                  <Box />
                  <HStack spacing={1}>
                    <Button
                      size="sm"
                      variant="ghost"
                      color="#a3a3a3"
                      fontWeight="normal"
                      px={3}
                      h="28px"
                      borderRadius="6px"
                      onClick={handleDiscard}
                      _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }}
                      _active={{ bg: 'rgba(255,255,255,0.08)' }}
                      _focus={{ boxShadow: 'none' }}
                    >
                      Discard
                    </Button>
                    <Button
                      size="sm"
                      bg="#fafafa"
                      color="#0a0a0a"
                      fontWeight="600"
                      px={3}
                      h="28px"
                      borderRadius="full"
                      onClick={handleApprove}
                      _hover={{ opacity: 0.85 }}
                      _active={{ opacity: 0.75 }}
                      _focus={{ boxShadow: 'none' }}
                    >
                      Approve
                    </Button>
                  </HStack>
                </>
              )}

              {generatePhase === 'approving' && (
                <>
                  <Box />
                  <HStack spacing={1}>
                    <Button
                      size="sm"
                      variant="ghost"
                      color="rgba(255,255,255,0.2)"
                      fontWeight="normal"
                      px={3}
                      h="28px"
                      borderRadius="6px"
                      _focus={{ boxShadow: 'none' }}
                      isDisabled
                    >
                      Discard
                    </Button>
                    <Button
                      size="sm"
                      bg="#fafafa"
                      color="#0a0a0a"
                      fontWeight="600"
                      px={3}
                      h="28px"
                      borderRadius="full"
                      isLoading
                      loadingText="Approving…"
                      _disabled={{ opacity: 0.55 }}
                      _focus={{ boxShadow: 'none' }}
                    >
                      Approve
                    </Button>
                  </HStack>
                </>
              )}
            </>
          ) : (
            /* Manual mode footer */
            <>
              {showPreview ? (
                <Button
                  variant="outline"
                  size="sm"
                  color="#a3a3a3"
                  borderColor="rgba(255,255,255,0.12)"
                  borderRadius="full"
                  fontSize="sm"
                  leftIcon={<LuRefreshCw size={14} />}
                  onClick={handlePreview}
                  isLoading={isPreviewing}
                  loadingText="Refreshing"
                  isDisabled={isGated || (!subject.trim() && !body.trim()) || ((messageType === 'new_email' || messageType === 'linkedin_inmail') && !subject.trim())}
                  _hover={{ bg: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.22)', color: '#fafafa' }}
                  _focus={{ boxShadow: 'none' }}
                >
                  Refresh preview
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  color="#a3a3a3"
                  borderColor="rgba(255,255,255,0.12)"
                  borderRadius="full"
                  fontSize="sm"
                  onClick={handlePreview}
                  isLoading={isPreviewing}
                  loadingText="Loading"
                  isDisabled={isGated || (!subject.trim() && !body.trim()) || ((messageType === 'new_email' || messageType === 'linkedin_inmail') && !subject.trim())}
                  _hover={{ bg: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.22)', color: '#fafafa' }}
                  _focus={{ boxShadow: 'none' }}
                >
                  Try on 5 rows
                </Button>
              )}
              <HStack spacing={2}>
                <Button variant="ghost" size="sm" color="#a3a3a3" borderRadius="full" _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }} _focus={{ boxShadow: 'none' }} onClick={onClose}>Cancel</Button>
                <Button
                  size="sm"
                  bg="#fafafa"
                  color="#0a0a0a"
                  borderRadius="full"
                  fontWeight="600"
                  onClick={handleSave}
                  isLoading={isSaving}
                  isDisabled={!typeOfMessage || (!subject.trim() && !body.trim()) || ((messageType === 'new_email' || messageType === 'linkedin_inmail') && !subject.trim())}
                  _hover={{ opacity: 0.85 }}
                  _disabled={{ opacity: 0.35, cursor: 'not-allowed' }}
                  _focus={{ boxShadow: 'none' }}
                >
                  Save
                </Button>
              </HStack>
            </>
          )}
        </HStack>
        )}
      </Box>

      <Modal isOpen={showAddSignatureModal} onClose={() => { setShowAddSignatureModal(false); setEditingSignatureId(null); setAddSignatureDefaultForNewMessage(false); setAddSignatureDefaultForFollowUps(false); }}>
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent
          bg="#0a0a0a"
          border="1px solid rgba(255,255,255,0.10)"
          borderRadius="12px"
          maxW="400px"
          mx={4}
          boxShadow="0 8px 32px rgba(0,0,0,0.6)"
        >
          <ModalHeader py={3} px={4} borderBottom="1px solid rgba(255,255,255,0.08)">
            <HStack justify="space-between" w="100%">
              <Text color="#fafafa" fontSize="md" fontWeight="700" letterSpacing="-0.01em">{editingSignatureId ? 'Edit signature' : 'Add signature'}</Text>
              <IconButton
                icon={<FiX />}
                onClick={() => { setShowAddSignatureModal(false); setEditingSignatureId(null); setAddSignatureDefaultForNewMessage(false); setAddSignatureDefaultForFollowUps(false); }}
                variant="ghost"
                color="#a3a3a3"
                fontSize="20px"
                size="sm"
                aria-label="Close"
                transition="all 0.25s ease-in-out"
                _hover={{ transform: "rotate(90deg)", fontSize: "25px", color: "red.400", bg: "transparent" }}
                _focus={{ boxShadow: 'none', outline: 'none' }}
                _focusVisible={{ boxShadow: 'none', outline: 'none' }}
              />
            </HStack>
          </ModalHeader>
          <ModalBody py={4} px={5} pb={2}>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel color="#a3a3a3" fontSize="sm" mb={1.5} fontWeight="medium">Name</FormLabel>
                <Input
                  value={addSignatureName}
                  onChange={(e) => setAddSignatureName(e.target.value)}
                  placeholder="Set signature name"
                  size="sm"
                  h="36px"
                  bg="#111111"
                  border="1px solid"
                  borderColor="rgba(255,255,255,0.12)"
                  color="#fafafa"
                  borderRadius="md"
                  _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none', outline: 'none' }}
                  _focusVisible={{ boxShadow: 'none', outline: 'none' }}
                  _placeholder={{ color: 'rgba(255,255,255,0.25)' }}
                />
              </FormControl>
              <FormControl>
                <FormLabel color="#a3a3a3" fontSize="sm" mb={1.5} fontWeight="medium">Content</FormLabel>
                <Textarea
                  value={addSignatureContent}
                  onChange={(e) => setAddSignatureContent(e.target.value)}
                  size="sm"
                  bg="#111111"
                  border="1px solid"
                  borderColor="rgba(255,255,255,0.12)"
                  color="#fafafa"
                  borderRadius="md"
                  minH="72px"
                  py={1}
                  resize="vertical"
                  _focus={{ borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none', outline: 'none' }}
                  _focusVisible={{ boxShadow: 'none', outline: 'none' }}
                  _placeholder={{ color: 'rgba(255,255,255,0.25)' }}
                />
              </FormControl>
              <HStack spacing={3} align="flex-start" pt={1} flexWrap="wrap">
                <Checkbox
                  colorScheme="whiteAlpha"
                  isChecked={addSignatureDefaultForNewMessage}
                  onChange={(e) => setAddSignatureDefaultForNewMessage(e.target.checked)}
                  size="sm"
                  _focus={{ boxShadow: 'none' }}
                  _focusVisible={{ boxShadow: 'none' }}
                >
                  <Text color="#a3a3a3" fontSize="sm">Set default for new messages</Text>
                </Checkbox>
                <Checkbox
                  colorScheme="whiteAlpha"
                  isChecked={addSignatureDefaultForFollowUps}
                  onChange={(e) => setAddSignatureDefaultForFollowUps(e.target.checked)}
                  size="sm"
                  _focus={{ boxShadow: 'none' }}
                  _focusVisible={{ boxShadow: 'none' }}
                >
                  <Text color="#a3a3a3" fontSize="sm">Set default for follow-ups</Text>
                </Checkbox>
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter py={3} px={5} gap={2} borderTop="1px solid rgba(255,255,255,0.08)">
            <Button variant="ghost" size="sm" color="#a3a3a3" borderRadius="full" fontSize="sm" onClick={() => { setShowAddSignatureModal(false); setEditingSignatureId(null); }} _hover={{ color: '#fafafa', bg: 'rgba(255,255,255,0.06)' }} _focus={{ boxShadow: 'none', outline: 'none' }} _focusVisible={{ boxShadow: 'none', outline: 'none' }}>
              Cancel
            </Button>
            <Button size="sm" bg="#fafafa" color="#0a0a0a" borderRadius="full" fontWeight="600" onClick={handleAddSignature} _hover={{ opacity: 0.85 }} _focus={{ boxShadow: 'none', outline: 'none' }} _focusVisible={{ boxShadow: 'none', outline: 'none' }}>
              {editingSignatureId ? 'Save' : 'Add'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {warningData && (
        <CreditWarningModal
          isOpen={isWarningOpen}
          onClose={closeWarning}
          operationType={warningData.operation_type}
          creditsNeeded={warningData.credits_needed}
          currentBalance={warningData.balance}
          onConfirm={handleConfirm}
          onOptOut={handleOptOut}
          userSubscriptionStatus={userSubscriptionStatus}
          allowsOverageProceed={warningData.preview_allows_overage_proceed === true}
          overageCreditsForRequest={warningData.overage_credits_for_request}
          overagePricePerCreditCents={warningData.overage_price_per_credit_cents}
        />
      )}
    </Box>
  );
};

export default MessageDraftingPanel;